import * as THREE from "three";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";
import {
  DECAY_DEFAULT_S,
  DEFAULT_FIELD_FADE,
  DEFAULT_PITCH_INDEX,
  DELAY_DEFAULT_S,
  ROUTER_HOLD_DEFAULT_S,
  FILL_RING_COLOR,
  LAMP_DEFAULT_HUE,
  LAMP_DEFAULT_SAT,
  RAIL_MAX_POINTS,
  RAIL_SEGMENTS,
  SELECTION_FRAME_COLOR,
  KNOB_DEFAULT_VALUE,
  KNOB_MIN_DEFAULT,
  KNOB_MAX_DEFAULT,
} from "./constants";
import { Emitter, ghostMeshFromEntry as emitterGhostMesh } from "./emitters";
import { BoxCornerResizeDrag, GizmoHandle, RotateDrag, rotateSnapSpec, RotateSnapGuide, scaleParamsFromLocal, SeqCornerResizeDrag } from "./gizmo";
import {
  CURVE_DEFAULT_CYCLE,
  defaultOrbitParams,
  defaultParams,
  defaultSeq,
  ghostMeshFromEntry as objectGhostMesh,
  insertRailPointIndex,
  makeGroup,
  PlacedObject,
  sampleRailCenterline,
} from "./objects";
import { PORTS } from "./connections";
import { buildClipboard, Clipboard, pasteClipboard } from "./clipboard";
import { AppContext, NumericParam, Tool } from "./appContext";
import { fatLineMaterial } from "./fatLine";
import { PortsApi } from "./ui/ports";
import { showActionModal } from "./ui/modal";
import { UndoStackApi } from "./undoStack";
import { EntityIndex } from "./entityIndex";
import { isEmitterTool, ToolSpawnApi } from "./toolSpawn";
import { resolveDrillSelection, transformPathFromRoot } from "./selectionDrill";
import { clampPlace, snapGrid, snapPos } from "./snap";
import { isTitleActive } from "./title";

const RAIL_START_PREVIEW_COLOR = 0x4a5560;

interface PointerDeps {
  canvas: HTMLCanvasElement;
  ports: PortsApi;
  undo: UndoStackApi;
  entities: EntityIndex;
  tools: ToolSpawnApi;
  getActiveTool(): Tool | null;
  gizmoIs2D(): boolean;
  getClipboard(): Clipboard | null;
  setClipboard(cb: Clipboard | null): void;
  uiView(): boolean;
  touchPlayActive(): boolean;
  showPaletteHint(tool: Tool): void;
  nodeSubmenu: {
    toolAt(x: number, y: number): Tool | null;
    setHover(tool: Tool | null): void;
    close(): void;
  };
  bindPanel(): void;
  refreshGizmo(): void;
  lockPanelOnTouch(ev: PointerEvent): void;
  persistObjects(): void;
  persistConnections(): void;
  syncEmitters(): void;
  settableParams(id: string): NumericParam[];
  togglePitchQuantizeClass(objId: string, pitchClass: number): void;
  isClaimLocked(id: string): boolean;
  isRadialMode(): boolean;
  openRadial(clientX: number, clientY: number): void;
  deletionLocked(): boolean;
  sendButton(id: string, phase: "press" | "release"): void;
  roomActive(): boolean;
  onSemiSelect(rootId: string): void;
}

export interface PointerApi {
  hitTestIds(ev: PointerEvent): string[];
  showArmedPreview(): void;
  hideArmedPreview(): void;
  beginPaletteDrag(tool: Tool, ev: PointerEvent, tapArms?: boolean): void;
  createEntityAt(tool: Tool, clientX: number, clientY: number): string | null;
  beginNodeMenuDrag(ev: PointerEvent, wasOpen: boolean): void;
  beginPasteDrag(ev: PointerEvent): void;
  placeToolAt(tool: Tool, clientX: number, clientY: number): boolean;
  pasteAt(clientX: number, clientY: number): boolean;
  seedToolAtAnchor(tool: Tool, clientX: number, clientY: number): boolean;
  consumeTouchPlayOneShots(): string[];
  getTouchPlayRailInputs(): TouchPlayRailInput[];
  clearTouchPlayState(): void;
  syncGizmoHandles(): void;
}

export interface TouchPlayRailInput {
  id: string;
  count: number;
  avgT: number;
  volScale: number;
}

/**
 * The canvas/keyboard interaction state machine: placement previews, entity
 * drag / cycle-select, gizmo drags (rotate / scale / rail points),
 * rail drawing, marquee grouping, simple-mode gestures, palette / paste
 * drag-to-place, screen-edge delete bands, and the editing keyboard shortcuts.
 */
export function initPointer(ctx: AppContext, deps: PointerDeps): PointerApi {
  const { scene, rig, selectionFrames, particles, field, emitterField, gizmo, audio, graph, widgets, settings, spawn, emitterSpawn } = ctx;
  const {
    canvas,
    ports,
    getActiveTool,
    gizmoIs2D,
    getClipboard,
    setClipboard,
    uiView,
    touchPlayActive,
    showPaletteHint,
    nodeSubmenu,
    bindPanel,
    refreshGizmo,
    lockPanelOnTouch,
    persistObjects,
    persistConnections,
    syncEmitters,
    settableParams,
    togglePitchQuantizeClass,
    isClaimLocked,
    isRadialMode,
    openRadial,
    deletionLocked,
    sendButton,
    roomActive,
    onSemiSelect,
  } = deps;
  const { pushUndo, pushSelectionUndo, discardLastUndo, recordBefore, syncCopyPasteButtons, beginEdit, undo, redo } = deps.undo;
  const {
    objectById,
    emitterById,
    isTransformParent,
    transformParentId,
    topmostGroupOf,
    selectedGroupContains,
    collectAllDescendants,
    coMoveDescendants,
  } = deps.entities;
  const { resetObjectSpawn, resetEmitterSpawn, setActiveTool, disarmTool, advanceSpawnPitchCycle } = deps.tools;

  const raycaster = new THREE.Raycaster();
  const groundPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  const dragPlane = new THREE.Plane();
  const placePlane = new THREE.Plane();
  const camDir = new THREE.Vector3();
  const camPos = new THREE.Vector3();
  const hit = new THREE.Vector3();

  /**
   * Builds the picking NDC from the canvas's own displayed rect, not
   * window.innerWidth/Height: on mobile the layout viewport (100dvh canvas)
   * and innerHeight can disagree mid address-bar transition, and the rect is
   * what the GL image is actually stretched onto.
   */
  function setRayAt(clientX: number, clientY: number): void {
    const rect = canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    raycaster.setFromCamera(ndc, rig.activeCamera);
  }

  function setRay(ev: PointerEvent): void {
    setRayAt(ev.clientX, ev.clientY);
  }

  function pointerPx(ev: PointerEvent): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
  }

  function nearestPlacementCenter(): THREE.Vector3 | null {
    rig.activeCamera.getWorldPosition(camPos);
    let best: THREE.Vector3 | null = null;
    let bestD = Infinity;
    for (const o of field.list()) {
      const d = camPos.distanceToSquared(o.position);
      if (d < bestD) {
        bestD = d;
        best = o.position;
      }
    }
    return best;
  }

  /**
   * Fixes the plane a placement preview lives on for the whole gesture: the z=0
   * ground in 2D (and in 3D when no objects exist yet), otherwise a screen-parallel
   * plane through the placed object nearest the camera, so 3D placement lands at
   * the depth of existing objects.
   */
  function setupPlacePlane(): void {
    const center = rig.canPlace() ? null : nearestPlacementCenter();
    if (!center) {
      placePlane.copy(groundPlane);
      return;
    }
    rig.activeCamera.getWorldDirection(camDir);
    placePlane.setFromNormalAndCoplanarPoint(camDir, center);
  }

  function placePoint(): THREE.Vector3 | null {
    if (!raycaster.ray.intersectPlane(placePlane, hit)) {
      return null;
    }
    const p = hit.clone();
    clampPlace(p, gizmoIs2D());
    return p;
  }

  /**
   * Plane a drag follows: z=0 ground in 2D, a screen-parallel plane (camera
   * view direction as normal) through the grabbed position in 3D, so 3D drags
   * move the object as seen on screen instead of pinning it to the floor.
   */
  function setupDragPlane(position: THREE.Vector3): void {
    if (rig.canPlace()) {
      dragPlane.copy(groundPlane);
    } else {
      rig.activeCamera.getWorldDirection(camDir);
      dragPlane.setFromNormalAndCoplanarPoint(camDir, position);
    }
  }

  function dragPoint(): THREE.Vector3 | null {
    return raycaster.ray.intersectPlane(dragPlane, hit) ? hit.clone() : null;
  }

  let placing = false;
  let placingKind: "object" | "emitter" = "object";
  let dragging = false;
  let dragKind: "object" | "emitter" = "object";
  let dragMoved = false;
  const dragOffset = new THREE.Vector3();
  let rotateDrag: RotateDrag | null = null;
  let rotateGuide: RotateSnapGuide | null = null;
  let scaling = false;
  let scaleObj: PlacedObject | null = null;
  let scaleHandleIndex = 0;
  let boxResizeDrag: BoxCornerResizeDrag | null = null;
  let seqResizeDrag: SeqCornerResizeDrag | null = null;
  let railPointDrag: { obj: PlacedObject; pointIndex: number } | null = null;

  interface RailHandlePending {
    obj: PlacedObject;
    pointIndex: number;
    startX: number;
    startY: number;
    curX: number;
    curY: number;
    timerId: ReturnType<typeof setTimeout>;
    showTimerId: ReturnType<typeof setTimeout>;
  }
  let railHandlePending: RailHandlePending | null = null;

  interface RailDuplicateDrag {
    obj: PlacedObject;
    originalPoints: THREE.Vector2[];
    sourceIndex: number;
    newIndex: number;
  }
  let railDuplicateDrag: RailDuplicateDrag | null = null;

  interface WidgetOp {
    kind: "button" | "knob" | "array" | "curve" | "pitchQuantize";
    obj: PlacedObject;
    paramName: string;
    grabValue: number;
    moved: boolean;
    startX: number;
    startY: number;
    seqEdited: boolean;
    curveLastIndex: number;
    curveLastValue: number;
  }
  const widgetOps = new Map<number, WidgetOp>();

  /**
   * Screen-space drag sensitivity of a rotary knob, in value units per pixel.
   * The two axes sum at equal per-pixel sensitivity (rightward + upward raise,
   * leftward + downward lower) and the result clamps to [0,1] — so up, right and
   * up-right all raise the value comparably, while a 45° down-right drag nets zero
   * (right and down cancel). Camera-independent, so a knob turns the same way from
   * any 3D viewpoint.
   */
  const KNOB_SENS = 1 / 180;

  /**
   * Starts a widget-control gesture (button press / knob grab), keyed by pointer
   * id so simultaneous touches each drive their own widget. The gesture owns its
   * pointer until release: selection, body drag and placement never see it. A
   * push button fires its single pulseOut and raises its held valueOut on the
   * down edge here; release clears the held value. A toggle button only sinks the
   * cap here — its latch flip and the off→on pulseOut resolve on release. A knob
   * records the grabbed param's current [0,1] value as the drag's anchor.
   */
  function beginWidgetOp(obj: PlacedObject, paramName: string, ev: PointerEvent): void {
    const semiRoot: string | null =
      touchPlayOn() && roomActive() ? transformPathFromRoot(obj.id, transformParentId)[0] : null;
    if (isClaimLocked(semiRoot ?? obj.id)) {
      if (!touchPlayOn()) {
        emitterField.deselect();
        field.select(obj);
        bindPanel();
      }
      return;
    }
    if (semiRoot !== null) {
      onSemiSelect(semiRoot);
    }
    if (obj.type === "button") {
      if (!touchPlayOn()) {
        emitterField.deselect();
        field.select(obj);
        disarmTool();
        bindPanel();
        lockPanelOnTouch(ev);
      }
      if (obj.buttonMode === "push") {
        widgets.press(obj.id);
        sendButton(obj.id, "press");
      }
      field.setButtonPressed(obj.id, true);
      widgetOps.set(ev.pointerId, { kind: "button", obj, paramName: "", grabValue: 0, moved: false, startX: ev.clientX, startY: ev.clientY, seqEdited: false, curveLastIndex: -1, curveLastValue: 0 });
      return;
    }
    if (obj.type === "array" || obj.type === "curve") {
      if (!touchPlayOn()) {
        emitterField.deselect();
        field.select(obj);
        disarmTool();
        bindPanel();
        lockPanelOnTouch(ev);
      }
      rig.suspendControls();
      const op: WidgetOp = { kind: obj.type, obj, paramName: "", grabValue: 0, moved: false, startX: ev.clientX, startY: ev.clientY, seqEdited: false, curveLastIndex: -1, curveLastValue: 0 };
      widgetOps.set(ev.pointerId, op);
      paintSeqAt(op, ev);
      return;
    }
    if (obj.type === "pitchQuantize") {
      if (!touchPlayOn()) {
        emitterField.deselect();
        field.select(obj);
        disarmTool();
        bindPanel();
        lockPanelOnTouch(ev);
      }
      const pitchClass = Number(paramName.slice(2));
      if (Number.isInteger(pitchClass) && pitchClass >= 0 && pitchClass < 12) {
        togglePitchQuantizeClass(obj.id, pitchClass);
      }
      widgetOps.set(ev.pointerId, { kind: "pitchQuantize", obj, paramName, grabValue: 0, moved: false, startX: ev.clientX, startY: ev.clientY, seqEdited: false, curveLastIndex: -1, curveLastValue: 0 });
      return;
    }
    const param = settableParams(obj.id).find((p: NumericParam): boolean => p.name === paramName);
    widgetOps.set(ev.pointerId, {
      kind: "knob",
      obj,
      paramName,
      grabValue: param ? param.get() : 0,
      moved: false,
      startX: ev.clientX,
      startY: ev.clientY,
      seqEdited: false,
      curveLastIndex: -1,
      curveLastValue: 0,
    });
  }

  /**
   * Paints the array cell / curve sample under the current ray. For a curve drag
   * the samples between the previous and current index are linearly interpolated
   * so a fast sweep leaves no gaps; the array sets just the touched column. The
   * first paint of a gesture opens a single undo step.
   */
  function paintSeqAt(op: WidgetOp, ev: PointerEvent): void {
    setRay(ev);
    if (op.kind === "array") {
      const cell = field.arrayCellAt(op.obj.id, raycaster);
      if (!cell) {
        return;
      }
      if (!op.seqEdited) {
        op.seqEdited = true;
        beginEdit(`seq:${op.obj.id}`);
      }
      field.setArrayCell(op.obj.id, cell.col, cell.row);
      return;
    }
    if (op.kind === "curve") {
      const sample = field.curveSampleAt(op.obj.id, raycaster);
      if (!sample) {
        return;
      }
      if (!op.seqEdited) {
        op.seqEdited = true;
        beginEdit(`seq:${op.obj.id}`);
      }
      if (op.curveLastIndex >= 0 && op.curveLastIndex !== sample.index) {
        const step = op.curveLastIndex < sample.index ? 1 : -1;
        for (let i = op.curveLastIndex + step; i !== sample.index; i += step) {
          const t = (i - op.curveLastIndex) / (sample.index - op.curveLastIndex);
          field.setCurveSample(op.obj.id, i, op.curveLastValue + (sample.value - op.curveLastValue) * t);
        }
      }
      field.setCurveSample(op.obj.id, sample.index, sample.value);
      op.curveLastIndex = sample.index;
      op.curveLastValue = sample.value;
    }
  }

  function updateWidgetOp(op: WidgetOp, ev: PointerEvent): void {
    if (op.kind === "array" || op.kind === "curve") {
      paintSeqAt(op, ev);
      return;
    }
    if (op.kind !== "knob") {
      return;
    }
    const param = settableParams(op.obj.id).find((p: NumericParam): boolean => p.name === op.paramName);
    if (!param) {
      return;
    }
    const dx = ev.clientX - op.startX;
    const dy = ev.clientY - op.startY;
    const next = Math.min(Math.max(op.grabValue + (dx - dy) * KNOB_SENS, 0), 1);
    if (next === param.get()) {
      return;
    }
    if (!op.moved) {
      op.moved = true;
      beginEdit(`knob:${op.obj.id}:${op.paramName}`);
    }
    param.set(next);
  }

  /**
   * Resolves a widget gesture on release. A toggle button commits its latch flip
   * only when the pointer is still over the same button (release outside cancels
   * with no flip), so a press begun by mistake can be aborted by sliding off; a
   * `null` event (pointercancel) always cancels. Its pulseOut fires only on the
   * off→on flip, never on→off. A push button pressed on the down edge (which
   * already fired its pulseOut and raised its held value) always releases that
   * held state here regardless of where the pointer is. The cap is restored to
   * rest unless the latch is on.
   */
  function endWidgetOp(op: WidgetOp, ev: PointerEvent | null): void {
    if (op.kind === "array" || op.kind === "curve") {
      rig.resumeControls();
      if (op.seqEdited) {
        persistObjects();
      }
      return;
    }
    if (op.kind === "button") {
      if (op.obj.buttonMode === "push") {
        widgets.release(op.obj.id);
        sendButton(op.obj.id, "release");
        field.setButtonPressed(op.obj.id, false);
        return;
      }
      const over = ev !== null && pointerOverButton(op.obj, ev);
      if (over) {
        pushUndo();
        recordBefore("obj", op.obj.id);
        op.obj.buttonOn = !op.obj.buttonOn;
        if (op.obj.buttonOn) {
          widgets.pulse(op.obj.id);
        }
        persistObjects();
      }
      field.setButtonPressed(op.obj.id, op.obj.buttonOn);
      return;
    }
    if (op.moved) {
      persistObjects();
    }
  }

  /** Restores a button's cap without firing its pulse / latch — used when a press converts into a body drag. */
  function cancelButtonPress(obj: PlacedObject): void {
    if (obj.buttonMode === "push") {
      widgets.release(obj.id);
      sendButton(obj.id, "release");
    }
    field.setButtonPressed(obj.id, obj.buttonMode === "toggle" && obj.buttonOn);
  }

  function pointerOverButton(obj: PlacedObject, ev: PointerEvent): boolean {
    setRay(ev);
    const hit = field.widgetControlHit(raycaster);
    return hit !== null && hit.obj.id === obj.id;
  }

  let gizmoMoved = false;
  let objClickPending = false;
  let lastPointerEv: PointerEvent | null = null;
  let lastRotateTap: { time: number; subjectId: string } | null = null;

  /**
   * A background tap in low-res radial mode (edit mode, nothing selected) arms
   * here on pointerdown and opens the radial on pointerup only if the pointer
   * barely moved — a real drag is left to OrbitControls for camera rotation.
   */
  let radialTapPending: { pointerId: number; startX: number; startY: number } | null = null;

  interface SimplePress {
    kind: "object" | "emitter";
    pointerId: number;
    startX: number;
    startY: number;
    moved: boolean;
    drillable: boolean;
  }
  let simplePress: SimplePress | null = null;
  const SIMPLE_TAP_SLOP_PX = 8;
  const MARQUEE_HINT_PX = 56;
  const MARQUEE_ARROW_OUTSET_PX = 22;

  function touchPlayOn(): boolean {
    return touchPlayActive();
  }

  const touchPlayOneShots: string[] = [];
  interface TouchPlayPointer {
    lastObjId: string | null;
    railId: string | null;
    arcT: number;
    volScale: number;
  }
  const touchPlayPointers = new Map<number, TouchPlayPointer>();

  function isSoundingType(type: PlacedObject["type"]): boolean {
    return type === "box" || type === "sphere" || type === "funnel";
  }

  /**
   * Closest-point distance between the ray (origin/normalized dir, s ≥ 0) and a
   * world-space segment, plus the segment parameter u ∈ [0,1] at that point.
   * Used to project a Touch Play finger onto a rail's centerline polyline.
   */
  function raySegmentClosest(origin: THREE.Vector3, dir: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3): { dist: number; u: number } {
    const seg = railTmpSeg.copy(b).sub(a);
    const r = railTmpR.copy(origin).sub(a);
    const ad = dir.dot(dir);
    const bd = dir.dot(seg);
    const cd = seg.dot(seg);
    const dd = dir.dot(r);
    const ed = seg.dot(r);
    const denom = ad * cd - bd * bd;
    let u = denom > 1e-9 ? (ad * ed - bd * dd) / denom : 0;
    u = Math.min(Math.max(u, 0), 1);
    let s = ad > 1e-9 ? (u * bd - dd) / ad : 0;
    if (s < 0) {
      s = 0;
    }
    const onRay = railTmpRay.copy(dir).multiplyScalar(s).add(origin);
    const onSeg = railTmpSegPt.copy(seg).multiplyScalar(u).add(a);
    return { dist: onRay.distanceTo(onSeg), u };
  }
  const railTmpSeg = new THREE.Vector3();
  const railTmpR = new THREE.Vector3();
  const railTmpRay = new THREE.Vector3();
  const railTmpSegPt = new THREE.Vector3();
  const railTmpWa = new THREE.Vector3();
  const railTmpWb = new THREE.Vector3();

  /**
   * Projects the current ray onto a rail's world-space centerline and returns the
   * normalized arc position [0,1] of the nearest point together with the
   * perpendicular distance from the ray to the centerline, or null when the
   * rail's live transform is unavailable. arcT drives the glissando position;
   * perpDist drives the off-axis volume falloff.
   */
  function railArcTFromRay(obj: PlacedObject): { arcT: number; perpDist: number } | null {
    const pos = field.liveOrBasePosition(obj.id);
    const rot = field.liveOrBaseRotation(obj.id);
    if (!pos || !rot || obj.railPoints.length < 2) {
      return null;
    }
    const center = sampleRailCenterline(obj.railPoints);
    railTmpWa.copy(center[0]).applyQuaternion(rot).add(pos);
    let best = Infinity;
    let bestT = 0;
    for (let j = 0; j < center.length - 1; j++) {
      railTmpWb.copy(center[j + 1]).applyQuaternion(rot).add(pos);
      const { dist, u } = raySegmentClosest(raycaster.ray.origin, raycaster.ray.direction, railTmpWa, railTmpWb);
      if (dist < best) {
        best = dist;
        bestT = (j + u) / RAIL_SEGMENTS;
      }
      railTmpWa.copy(railTmpWb);
    }
    return { arcT: bestT, perpDist: best };
  }

  /**
   * Off-axis rail volume envelope keyed on the touch's perpendicular distance
   * from the centerline, normalized by the rail's pick radius: full across the
   * inner 80%, a linear fade over the next 10%, silent beyond. Volume is
   * thus flat along the rail axis and only falls as the finger drifts sideways.
   */
  function railPerpVolume(perpDist: number): number {
    const ratio = perpDist / field.railPickRadius();
    if (ratio <= 0.8) {
      return 1;
    }
    if (ratio >= 0.9) {
      return 0;
    }
    return 1 - (ratio - 0.8) / 0.1;
  }

  /**
   * Resolves the nearest playable entity under the current ray for an active
   * play-mode finger and updates its state: a sounding body (box / sphere /
   * funnel) queues a one-shot the moment the finger enters it (down or slide-in),
   * suppressed while the finger stays on the same body; a rail registers /
   * refreshes its arc position for the sustained voice. Non-sounding occluders
   * (group containers / signal nodes) are skipped — a group box often sits at the
   * same depth as its members and would otherwise win the front hit by a hair and
   * swallow the tap — so the finger always reaches the sounding object it points
   * at. No playable hit clears the finger so a later re-entry re-fires.
   */
  function processTouchPlayHit(state: TouchPlayPointer): void {
    const playable = rawHits().find(
      (h: MergedHit): boolean =>
        h.kind === "object" && (h.item.type === "rail" || isSoundingType(h.item.type)),
    );
    if (!playable || playable.kind !== "object") {
      state.railId = null;
      state.lastObjId = null;
      return;
    }
    const obj = playable.item;
    if (obj.type === "rail") {
      const proj = railArcTFromRay(obj);
      if (proj !== null) {
        state.railId = obj.id;
        state.arcT = proj.arcT;
        state.volScale = railPerpVolume(proj.perpDist);
      } else {
        state.railId = null;
      }
      state.lastObjId = null;
      return;
    }
    state.railId = null;
    if (state.lastObjId !== obj.id) {
      touchPlayOneShots.push(obj.id);
      state.lastObjId = obj.id;
    }
  }

  /**
   * Empties the queued one-shot collision injections (box / sphere / funnel
   * touch-downs / slide-ins) so the sim frame fires each as a single note once.
   */
  function consumeTouchPlayOneShots(): string[] {
    if (touchPlayOneShots.length === 0) {
      return [];
    }
    const ids = touchPlayOneShots.slice();
    touchPlayOneShots.length = 0;
    return ids;
  }

  /**
   * Aggregates the live rail touches into one input per rail: count is the number
   * of fingers on that rail, avgT the mean of their arc positions.
   */
  function getTouchPlayRailInputs(): TouchPlayRailInput[] {
    if (touchPlayPointers.size === 0) {
      return [];
    }
    const byRail = new Map<string, { count: number; tSum: number; volSum: number }>();
    for (const st of touchPlayPointers.values()) {
      if (st.railId === null) {
        continue;
      }
      const acc = byRail.get(st.railId);
      if (acc) {
        acc.count++;
        acc.tSum += st.arcT;
        acc.volSum += st.volScale;
      } else {
        byRail.set(st.railId, { count: 1, tSum: st.arcT, volSum: st.volScale });
      }
    }
    const inputs: TouchPlayRailInput[] = [];
    for (const [id, acc] of byRail) {
      inputs.push({ id, count: acc.count, avgT: acc.tSum / acc.count, volScale: acc.volSum / acc.count });
    }
    return inputs;
  }

  function clearTouchPlayState(): void {
    touchPlayOneShots.length = 0;
    touchPlayPointers.clear();
  }

  interface RailDrawState {
    points: THREE.Vector3[];
    line: Line2;
    markers: THREE.Group;
  }
  let railDraw: RailDrawState | null = null;
  const RAIL_CONFIRM_DIST = 0.35;

  /**
   * Opens a rail drawing session: clicks append control points (preview curve
   * follows the cursor); re-clicking the last placed point (or Enter) commits,
   * Escape or a tap on a screen-edge band cancels. The session is its own
   * input mode — while open, canvas clicks neither select nor place.
   */
  function startRailDraw(): void {
    cancelRailDraw();
    hideRailStartPreview();
    const geometry = new LineGeometry();
    geometry.setPositions([0, 0, 0, 0, 0, 0]);
    const material = fatLineMaterial(RAIL_START_PREVIEW_COLOR);
    material.opacity = 0.9;
    material.resolution.set(window.innerWidth, window.innerHeight);
    const line = new Line2(geometry, material);
    line.visible = false;
    const markers = new THREE.Group();
    scene.add(line);
    scene.add(markers);
    railDraw = { points: [], line, markers };
  }

  function updateRailDrawPreview(cursor: THREE.Vector3 | null): void {
    if (!railDraw) {
      return;
    }
    const snappedCursor = cursor ? snapMove(cursor) : null;
    const pts = snappedCursor ? [...railDraw.points, snappedCursor] : [...railDraw.points];
    if (pts.length < 2) {
      railDraw.line.visible = false;
      return;
    }
    railDraw.line.visible = true;
    const sampled = sampleRailCenterline(pts.map((p: THREE.Vector3): THREE.Vector2 => new THREE.Vector2(p.x, p.y)));
    const flat: number[] = [];
    for (const p of sampled) {
      flat.push(p.x, p.y, p.z);
    }
    (railDraw.line.geometry as LineGeometry).setPositions(flat);
  }

  function addRailDrawPoint(point: THREE.Vector3): void {
    if (!railDraw || railDraw.points.length >= RAIL_MAX_POINTS) {
      return;
    }
    const snapped = snapMove(point).clone();
    railDraw.points.push(snapped);
    const marker = new THREE.Mesh(
      new THREE.SphereGeometry(0.12, 12, 8),
      new THREE.MeshBasicMaterial({ color: RAIL_START_PREVIEW_COLOR }),
    );
    marker.position.copy(snapped);
    railDraw.markers.add(marker);
    updateRailDrawPreview(null);
  }

  function cancelRailDraw(): void {
    if (!railDraw) {
      return;
    }
    scene.remove(railDraw.line);
    railDraw.line.geometry.dispose();
    (railDraw.line.material as THREE.Material).dispose();
    for (const child of railDraw.markers.children) {
      const mesh = child as THREE.Mesh;
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    scene.remove(railDraw.markers);
    railDraw = null;
  }

  /**
   * Commits the drawn control points as a rail object posed at their centroid
   * (points are stored centroid-relative so gizmo move/rotate compose cleanly).
   * Physics is fixed to bounce 0 / friction 0 — particles glide along the
   * groove — and the glide spans one octave from the default pitch index.
   * Fewer than two points keeps the session open instead of committing.
   */
  function commitRailDraw(): void {
    if (!railDraw || railDraw.points.length < 2) {
      return;
    }
    const centroid = new THREE.Vector3();
    for (const p of railDraw.points) {
      centroid.add(p);
    }
    centroid.divideScalar(railDraw.points.length);
    centroid.z = 0;
    const railPoints = railDraw.points.map(
      (p: THREE.Vector3): THREE.Vector2 => new THREE.Vector2(p.x - centroid.x, p.y - centroid.y),
    );
    cancelRailDraw();
    const obj: PlacedObject = {
      id: crypto.randomUUID(),
      type: "rail",
      position: centroid,
      rotation: new THREE.Quaternion(),
      restitution: 0,
      friction: 0,
      mass: settings.mass,
      massEnabled: false,
      killOnContact: false,
      collisionEnabled: true,
      pitchIndex: DEFAULT_PITCH_INDEX,
      timbreId: "tone",
      timbreIds: ["tone"],
      seqCycleLength: CURVE_DEFAULT_CYCLE,
      volume: 1,
      muted: false,
      fade: DEFAULT_FIELD_FADE,
      operator: "add",
      operand: 0,
      decayS: DECAY_DEFAULT_S,
      delayS: DELAY_DEFAULT_S,
      routerHoldS: ROUTER_HOLD_DEFAULT_S,
      routerHoldResetOnPulse: false,
      timerMode: "loop",
      knobValue: KNOB_DEFAULT_VALUE,
      knobMin: KNOB_MIN_DEFAULT,
      knobMax: KNOB_MAX_DEFAULT,
      knobInt: false,
      buttonMode: "push",
      buttonOn: false,
      buttonPulseOnReset: false,
      lampHue: LAMP_DEFAULT_HUE,
      lampSat: LAMP_DEFAULT_SAT,
      lampValue: 0,
      ...defaultSeq("rail"),
      params: defaultParams("rail"),
      scale: new THREE.Vector2(1, 1),
      arcDeg: 360,
      orbit: defaultOrbitParams(),
      railPoints,
      pitchIndexEnd: DEFAULT_PITCH_INDEX + 12,
      railTimbre: "sine",
      name: "",
    };
    pushUndo();
    const before = field.list().length;
    field.add(obj);
    if (field.list().length > before) {
      persistObjects();
    } else {
      discardLastUndo();
    }
    bindPanel();
  }

  let railStartMarker: THREE.Mesh | null = null;

  /**
   * Ghost marker for a rail's first control point, shown while a rail drag-to-place
   * gesture is in flight but no draw session is open yet, matching the in-draw
   * marker style so the prospective (snapped) start position reads the same as a
   * placed point.
   */
  function showRailStartPreview(at: THREE.Vector3): void {
    if (!railStartMarker) {
      railStartMarker = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 12, 8),
        new THREE.MeshBasicMaterial({ color: RAIL_START_PREVIEW_COLOR }),
      );
      scene.add(railStartMarker);
    }
    railStartMarker.position.copy(at);
  }

  function hideRailStartPreview(): void {
    if (!railStartMarker) {
      return;
    }
    scene.remove(railStartMarker);
    railStartMarker.geometry.dispose();
    (railStartMarker.material as THREE.Material).dispose();
    railStartMarker = null;
  }

  function showArmedPreview(): void {
    field.cancelPreview();
    emitterField.cancelPreview();
    hideRailStartPreview();
    const tool = getActiveTool();
    if (!tool || field.selectedObject() || emitterField.selectedEmitter()) {
      return;
    }
    let point: THREE.Vector3 | null = null;
    if (lastPointerEv) {
      setRay(lastPointerEv);
      setupPlacePlane();
      point = placePoint();
    }
    const at = point ?? new THREE.Vector3();
    if (isEmitterTool(tool)) {
      placingKind = "emitter";
      emitterField.beginPreview(emitterSpawn, at);
    } else {
      placingKind = "object";
      field.beginPreview(spawn, at);
    }
  }

  function hideArmedPreview(): void {
    field.cancelPreview();
    emitterField.cancelPreview();
    hideRailStartPreview();
  }

  type MergedHit =
    | { kind: "object"; item: PlacedObject; dist: number }
    | { kind: "emitter"; item: Emitter; dist: number };

  /**
   * Distance-ordered union of object + emitter hits, with selection gating: a hit on a
   * group member is rewritten to a hit on that member's topmost group, so a direct
   * canvas click on a child grabs the whole cluster instead. A hit on the currently
   * selected entity is never rewritten — a selected member must stay hittable for
   * its own drag/cycle handling. While a wire is pending no rewrite happens at all:
   * the wire targets the member itself (a group accepts no wires). Rewritten hits
   * dedupe to the group, keeping the nearest.
   */
  /**
   * Minimum tap target radius in canvas px = 5% of the canvas's shorter side.
   * Derived from the live canvas rect (not a hardcoded px) so a screen rotation
   * that swaps the short edge retargets it automatically.
   */
  function minTapRadiusPx(): number {
    const rect = canvas.getBoundingClientRect();
    return 0.05 * Math.min(rect.width, rect.height);
  }

  /**
   * Screen-space pick fallback for the selection/operation tap: when the
   * geometry raycast finds nothing, the nearest pickable body/emitter whose
   * projected center lies within the min-tap radius of the tap point is grabbed,
   * so a body that shrinks to a few pixels on screen stays tappable. Tap px and
   * projected px share the canvas-rect basis (so a non-fullscreen canvas or a
   * mobile address-bar transition can't skew them). Behind-camera candidates are
   * skipped. The returned `dist` is the camera distance so downstream sort/drill
   * logic compares it in the same units as geometry hits. Selection paths only —
   * Touch Play and wiring call `rawHits()` with no event and never reach here.
   */
  function screenSpaceFallbackHit(ev: PointerEvent): MergedHit | null {
    const rect = canvas.getBoundingClientRect();
    const tap = pointerPx(ev);
    const radius = minTapRadiusPx();
    rig.activeCamera.getWorldPosition(camPos);
    const candidates: { hit: MergedHit; screenDist: number }[] = [];
    const ndc = new THREE.Vector3();
    const collect = (item: PlacedObject | Emitter, center: THREE.Vector3, kind: "object" | "emitter"): void => {
      ndc.copy(center).project(rig.activeCamera);
      if (ndc.z > 1) {
        return;
      }
      const px = ((ndc.x + 1) / 2) * rect.width;
      const py = ((1 - ndc.y) / 2) * rect.height;
      const screenDist = Math.hypot(px - tap.x, py - tap.y);
      if (screenDist > radius) {
        return;
      }
      const entry: MergedHit = kind === "object"
        ? { kind: "object", item: item as PlacedObject, dist: camPos.distanceTo(center) }
        : { kind: "emitter", item: item as Emitter, dist: camPos.distanceTo(center) };
      candidates.push({ hit: entry, screenDist });
    };
    for (const c of field.pickCandidates()) {
      collect(c.item, c.center, "object");
    }
    for (const c of emitterField.pickCandidates()) {
      collect(c.item, c.center, "emitter");
    }
    if (candidates.length === 0) {
      return null;
    }
    candidates.sort((a: { hit: MergedHit; screenDist: number }, b: { hit: MergedHit; screenDist: number }): number =>
      a.screenDist - b.screenDist || a.hit.dist - b.hit.dist);
    return candidates[0].hit;
  }

  function rawHits(ev?: PointerEvent): MergedHit[] {
    const raw: MergedHit[] = [
      ...field.orderedHits(raycaster).map((h: { item: PlacedObject; dist: number }): MergedHit =>
        ({ kind: "object", item: h.item, dist: h.dist })),
      ...emitterField.orderedHits(raycaster).map((h: { item: Emitter; dist: number }): MergedHit =>
        ({ kind: "emitter", item: h.item, dist: h.dist })),
    ];
    if (raw.length === 0 && ev) {
      const fb = screenSpaceFallbackHit(ev);
      return fb ? [fb] : [];
    }
    return raw.sort((a: MergedHit, b: MergedHit): number => a.dist - b.dist);
  }

  function mergedHits(raw: MergedHit[] = rawHits()): MergedHit[] {
    const selected: PlacedObject | Emitter | null = field.selectedObject() ?? emitterField.selectedEmitter();
    const wiring = ports.isPending();
    const gated: MergedHit[] = [];
    for (const h of raw) {
      let entry: MergedHit = h;
      const top = topmostGroupOf(h.item.id);
      if (top && !wiring && h.item !== selected && !selectedGroupContains(h.item.id)) {
        const g = objectById(top);
        if (g) {
          entry = { kind: "object", item: g, dist: h.dist };
        }
      }
      const existing = gated.find((o: MergedHit): boolean => o.item === entry.item);
      if (existing) {
        existing.dist = Math.min(existing.dist, entry.dist);
      } else {
        gated.push(entry);
      }
    }
    return gated.sort((a: MergedHit, b: MergedHit): number => a.dist - b.dist);
  }

  /**
   * A pulse wire aimed at a binary/unary calc node finds no acceptable pin (it
   * has only value-ins; inc/reset live on the count node). Clicking such a calc
   * node mid-wire converts it to a count node so the wiring can land on
   * inc/reset — one undoable gesture, dropping the now-hidden operand edges. The
   * caller then focuses the node so the user picks the pin (no auto-connect:
   * count has two pulse-ins). A random calc is excluded: it already exposes a
   * pulse-in, so the wire lands on it directly with no conversion.
   */
  function convertValueToCountForPulse(hit: MergedHit): boolean {
    const port = ports.pendingPort();
    if (port === null || PORTS[port].dir !== "out" || PORTS[port].type !== "pulse") {
      return false;
    }
    if (hit.kind !== "object" || hit.item.type !== "calc" || hit.item.operator === "random") {
      return false;
    }
    pushUndo();
    recordBefore("obj", hit.item.id);
    hit.item.type = "count";
    hit.item.params = defaultParams("count");
    for (const e of graph.edgesTo(hit.item.id, "operand1In")) {
      graph.disconnect(e);
    }
    for (const e of graph.edgesTo(hit.item.id, "operand2In")) {
      graph.disconnect(e);
    }
    field.refreshMeshById(hit.item.id);
    persistObjects();
    persistConnections();
    bindPanel();
    return true;
  }

  function deleteSelectedEntity(): void {
    if (deletionLocked()) {
      return;
    }
    const selEmitter = emitterField.selectedEmitter();
    if (selEmitter) {
      graph.removeEntity(selEmitter.id);
      emitterField.deleteSelected();
      syncEmitters();
      persistConnections();
      bindPanel();
      return;
    }
    const selObject = field.selectedObject();
    if (!selObject) {
      return;
    }
    graph.removeEntity(selObject.id);
    field.deleteSelected();
    persistConnections();
    bindPanel();
    persistObjects();
  }

  type GroupDeleteChoice = "all" | "group" | "cancel";

  let groupDragOrigin: Map<string, { pos: THREE.Vector3; rot: THREE.Quaternion }> | null = null;

  /**
   * Records the pre-drag base pose of a transform parent (group or orbit) and its whole
   * subtree at the first drag movement, so an edge-drop delete can offer "parent only"
   * (children snap back) and "cancel" (everything snaps back) on top of the destructive default.
   */
  function captureDragOrigin(parent: PlacedObject): void {
    const ids = new Set<string>();
    collectAllDescendants(parent.id, ids, new Set<string>());
    ids.add(parent.id);
    groupDragOrigin = new Map();
    for (const id of ids) {
      const o = objectById(id);
      const em = o ? null : emitterById(id);
      const pos = o ? o.position : em ? em.position : null;
      const rot = o ? o.rotation : em ? em.rotation : null;
      if (pos && rot) {
        groupDragOrigin.set(id, { pos: pos.clone(), rot: rot.clone() });
      }
    }
  }

  function restoreGroupDragOrigin(): void {
    if (!groupDragOrigin) {
      return;
    }
    for (const [id, pose] of groupDragOrigin) {
      const o = objectById(id);
      const em = o ? null : emitterById(id);
      if (o) {
        o.position.copy(pose.pos);
        o.rotation.copy(pose.rot);
      } else if (em) {
        em.position.copy(pose.pos);
        em.rotation.copy(pose.rot);
      }
    }
  }

  function showGroupDeleteModal(onChoice: (choice: GroupDeleteChoice) => void): void {
    showActionModal<GroupDeleteChoice>("Delete group?", null, [
      { label: "Delete All", value: "all" },
      { label: "Delete Group Only", value: "group" },
      { label: "Cancel", value: "cancel", subtle: true },
    ], onChoice);
  }

  /**
   * Resolves a group dropped on a delete edge. "cancel" restores the whole subtree
   * to its pre-drag pose and pops the drag's undo entry (the scene is back to that
   * snapshot); "group" restores the children then deletes only the group node;
   * "all" deletes the group and every descendant entity.
   */
  function resolveGroupEdgeDelete(group: PlacedObject, choice: GroupDeleteChoice): void {
    if (choice === "cancel") {
      restoreGroupDragOrigin();
      groupDragOrigin = null;
      discardLastUndo();
      gizmo.sync();
      persistObjects();
      syncEmitters();
      return;
    }
    if (choice === "group") {
      restoreGroupDragOrigin();
      groupDragOrigin = null;
      deleteSelectedEntity();
      syncEmitters();
      return;
    }
    groupDragOrigin = null;
    const ids = new Set<string>();
    collectAllDescendants(group.id, ids, new Set<string>());
    for (const id of ids) {
      graph.removeEntity(id);
      if (objectById(id)) {
        field.removeById(id);
      } else {
        emitterField.removeById(id);
      }
    }
    deleteSelectedEntity();
    syncEmitters();
  }

  function selId(): string | null {
    return field.selectedObject()?.id ?? emitterField.selectedEmitter()?.id ?? null;
  }

  function deselectAll(): void {
    if (selId() !== null) {
      pushSelectionUndo();
    }
    field.deselect();
    emitterField.deselect();
  }

  function selectByEntityId(id: string): boolean {
    const obj = objectById(id);
    if (obj) {
      if (selId() !== id) {
        pushSelectionUndo();
      }
      emitterField.deselect();
      field.select(obj);
      disarmTool();
      return true;
    }
    const em = emitterById(id);
    if (em) {
      if (selId() !== id) {
        pushSelectionUndo();
      }
      field.deselect();
      emitterField.select(em);
      disarmTool();
      return true;
    }
    return false;
  }

  /**
   * Cycles selection through the distance-ordered union of object and emitter
   * hits: re-clicking the same spot advances front→back→…→last→deselect→front,
   * crossing object/emitter types within one stack. A click whose position is
   * not on the current selection resets to the nearest hit; an empty-space click
   * deselects everything. Before cyclic fallback, transform-chain drill logic
   * resolves one-step descent (parent → direct child on the hit path) using raw
   * ungated hits, so group/orbit hierarchies can be traversed by repeated clicks.
   * With no descent available, a selected child advances through its overlapping
   * siblings (the parent's children on the hit path, distance-ordered) before
   * deselecting, so a re-clicked stack inside a group cycles every member.
   */
  function cycleSelect(ev: PointerEvent): void {
    setRay(ev);
    const raw = rawHits(ev);
    const merged = mergedHits(raw);
    if (merged.length === 0) {
      deselectAll();
      bindPanel();
      return;
    }
    const cur = field.selectedObject() ?? emitterField.selectedEmitter();
    if (cur && (isTransformParent(cur.id) || transformParentId(cur.id) !== null)) {
      let descent: string | undefined;
      for (const h of raw) {
        if (h.item.id === cur.id) {
          continue;
        }
        const d = resolveDrillSelection(cur.id, h.item.id, transformParentId);
        if (d !== undefined && d !== null) {
          descent = d;
          break;
        }
      }
      if (descent !== undefined && selectByEntityId(descent)) {
        bindPanel();
        lockPanelOnTouch(ev);
        return;
      }
      const parentId = transformParentId(cur.id);
      if (descent === undefined && parentId !== null) {
        const siblings: string[] = [];
        for (const h of raw) {
          const child = resolveDrillSelection(parentId, h.item.id, transformParentId);
          if (child !== undefined && child !== null && !siblings.includes(child)) {
            siblings.push(child);
          }
        }
        const idx = siblings.indexOf(cur.id);
        if (idx >= 0) {
          if (idx < siblings.length - 1) {
            if (selectByEntityId(siblings[idx + 1])) {
              bindPanel();
              lockPanelOnTouch(ev);
              return;
            }
          } else {
            deselectAll();
            bindPanel();
            return;
          }
        }
      }
    }
    const pos = cur ? merged.findIndex((h: MergedHit): boolean => h.item === cur) : -1;
    const next = pos >= 0 ? (pos === merged.length - 1 ? null : merged[pos + 1]) : merged[0];
    if (next === null) {
      deselectAll();
    } else if (next.kind === "object") {
      if (selId() !== next.item.id) {
        pushSelectionUndo();
      }
      emitterField.deselect();
      field.select(next.item);
      disarmTool();
    } else {
      if (selId() !== next.item.id) {
        pushSelectionUndo();
      }
      field.deselect();
      emitterField.select(next.item);
      disarmTool();
    }
    bindPanel();
    if (next !== null) lockPanelOnTouch(ev);
  }

  function gizmoSubject(): PlacedObject | Emitter | null {
    return field.selectedObject() ?? emitterField.selectedEmitter();
  }

  /**
   * Simple-layer press: starts a tap-or-move gesture on the body under the ray.
   * Selection resolution is delegated to `cycleSelect` — the same path the full
   * editor uses — so Simple-mode focus/drill/cycle/deselect stay identical to
   * non-Simple mode. A press whose ray already lies on the current selection
   * keeps it selected and arms a drag (so dragging a focused group moves the
   * whole cluster); the cycle/drill only fires on a tap (no move). A press
   * elsewhere resolves the new selection immediately so the same press can drag
   * it. Gizmo handles and widget control caps keep priority so rotate stays
   * usable and the playable widgets stay tappable. Returns whether a gesture
   * started, so the caller captures the pointer.
   */
  function simplePointerDown(ev: PointerEvent): boolean {
    if (field.selectedObject() || emitterField.selectedEmitter()) {
      const handle = gizmo.pick(raycaster);
      if (handle) {
        if (tryRotateReset(handle)) return true;
        startGizmoDrag(handle, ev.clientX, ev.clientY);
        return true;
      }
    }
    const raw = rawHits(ev);
    const merged = mergedHits(raw);
    const widgetHit = field.widgetControlHit(raycaster);
    if (widgetHit && (merged.length === 0 || merged[0].item === widgetHit.obj || widgetHit.dist <= merged[0].dist)) {
      beginWidgetOp(widgetHit.obj, widgetHit.paramName, ev);
      return true;
    }
    if (merged.length === 0) {
      deselectAll();
      bindPanel();
      return false;
    }
    const cur = field.selectedObject() ?? emitterField.selectedEmitter();
    const pressOnCurrent = cur !== null && raw.some((h: MergedHit): boolean => h.item === cur);
    if (!pressOnCurrent) {
      cycleSelect(ev);
    }
    if (field.selectedObject() === null && emitterField.selectedEmitter() === null) {
      return false;
    }
    const kind: "object" | "emitter" = field.selectedObject() !== null ? "object" : "emitter";
    simplePress = { kind, pointerId: ev.pointerId, startX: ev.clientX, startY: ev.clientY, moved: false, drillable: pressOnCurrent };
    return true;
  }

  function clearRotateGuide(): void {
    if (!rotateGuide) {
      return;
    }
    scene.remove(rotateGuide.group);
    rotateGuide.dispose();
    rotateGuide = null;
  }

  function applyGizmoRotation(q: THREE.Quaternion): void {
    const obj = field.selectedObject();
    if (obj) {
      if (isTransformParent(obj.id)) {
        coMoveDescendants(obj, obj.position, q);
      }
      field.editSelected({ rotation: q });
    } else if (emitterField.selectedEmitter()) {
      emitterField.editSelected({ rotation: q });
      particles.setEmitters(emitterField.list());
    }
  }

  function snapMove(pos: THREE.Vector3): THREE.Vector3 {
    const snapped = snapPos(pos, snapGrid(settings.editSnap), gizmoIs2D());
    clampPlace(snapped, gizmoIs2D());
    return snapped;
  }

  /**
   * Local-space anchor whose world position a move should land on the grid,
   * relative to the object's origin, or null to snap the origin itself. A box
   * uses its canonical min corner (sign (-1,-1,-1) × half-extents); a rail uses
   * its first control point, because the origin is the points' centroid — an
   * off-grid average — so origin-snapping would knock the grid-aligned control
   * points off the grid by a fractional delta. Centrally symmetric types
   * (sphere/funnel/widgets) and groups have no such anchor and snap their origin.
   */
  function moveAnchorLocal(obj: PlacedObject): THREE.Vector3 | null {
    if (obj.type === "box") {
      return new THREE.Vector3(-obj.params.x, -obj.params.y, -obj.params.z);
    }
    if (obj.type === "rail" && obj.railPoints.length > 0) {
      const p = obj.railPoints[0];
      return new THREE.Vector3(p.x, p.y, 0);
    }
    return null;
  }

  /**
   * Move-snap for a placed object's origin. Types with a move anchor (box corner,
   * rail start point) snap that anchor's world position onto the grid — carried
   * through the live rotation — and back-compute the origin, so the anchor rather
   * than the origin lands on a grid line; for a box this keeps the resize anchor
   * grid-aligned regardless of cell-count parity. Every other type snaps its
   * origin directly.
   */
  function snapObjectMove(obj: PlacedObject, rawCenter: THREE.Vector3): THREE.Vector3 {
    const anchorLocal = moveAnchorLocal(obj);
    if (anchorLocal === null) {
      return snapMove(rawCenter);
    }
    const grid = snapGrid(settings.editSnap);
    const anchorOffset = anchorLocal.applyQuaternion(obj.rotation);
    const snappedAnchor = snapPos(rawCenter.clone().add(anchorOffset), grid, gizmoIs2D());
    const origin = snappedAnchor.sub(anchorOffset);
    clampPlace(origin, gizmoIs2D());
    return origin;
  }

  function gizmoPersist(): void {
    const obj = field.selectedObject();
    if (obj) {
      persistObjects();
      if (obj.type === "group") {
        syncEmitters();
      }
    } else if (emitterField.selectedEmitter()) {
      syncEmitters();
    }
  }

  /**
   * Returns true when this pointerdown on a rotate handle is the second of a
   * double-tap (< 300 ms, same subject), resets the subject rotation to identity,
   * and clears the tap record so a subsequent single tap starts fresh.
   */
  function tryRotateReset(handle: GizmoHandle): boolean {
    if (handle.kind !== "rotate") {
      lastRotateTap = null;
      return false;
    }
    const subject = gizmoSubject();
    if (!subject) return false;
    const now = performance.now();
    if (lastRotateTap && lastRotateTap.subjectId === subject.id && now - lastRotateTap.time < 300) {
      lastRotateTap = null;
      pushUndo();
      applyGizmoRotation(new THREE.Quaternion());
      gizmo.sync();
      bindPanel();
      gizmoPersist();
      return true;
    }
    lastRotateTap = { time: now, subjectId: subject.id };
    return false;
  }

  function startGizmoDrag(handle: GizmoHandle, clientX: number, clientY: number): void {
    gizmoMoved = false;
    gizmo.setHover(handle);
    const subject = gizmoSubject();
    if (!subject) {
      return;
    }
    if (isClaimLocked(subject.id)) {
      return;
    }
    if (handle.kind === "rotate") {
      const axisVec = gizmo.rotateAxisVec(handle.axis);
      if (!axisVec) {
        return;
      }
      const snap = rotateSnapSpec(gizmo.ringRadius());
      rotateDrag = new RotateDrag(axisVec, subject.position, subject.rotation, raycaster.ray, snap);
      rotateGuide = new RotateSnapGuide(handle.axis, rotateDrag.guideFrame(), snap);
      scene.add(rotateGuide.group);
      return;
    }
    if (handle.kind === "railPoint") {
      const obj = field.selectedObject();
      if (!obj) {
        return;
      }
      const planeNormal = new THREE.Vector3(0, 0, 1).applyQuaternion(obj.rotation);
      dragPlane.setFromNormalAndCoplanarPoint(planeNormal, obj.position);
      if (obj.railPoints.length >= RAIL_MAX_POINTS) {
        railPointDrag = { obj, pointIndex: handle.index };
        return;
      }
      const timerId = setTimeout((): void => {
        if (!railHandlePending) {
          return;
        }
        const pending = railHandlePending;
        railHandlePending = null;
        clearTimeout(pending.showTimerId);
        hideHoldRing();
        pushUndo();
        const originalPoints = [...pending.obj.railPoints];
        const srcPt = originalPoints[pending.pointIndex];
        const newPoints = [...originalPoints];
        newPoints.splice(pending.pointIndex + 1, 0, new THREE.Vector2(srcPt.x, srcPt.y));
        field.updateRailPoints(pending.obj.id, newPoints);
        refreshRailHalo();
        refreshGizmo();
        gizmo.sync();
        railDuplicateDrag = {
          obj: pending.obj,
          originalPoints,
          sourceIndex: pending.pointIndex,
          newIndex: pending.pointIndex + 1,
        };
        updateDeleteEdgeHighlight(pending.curX, pending.curY);
      }, RAIL_DUP_HOLD_MS);
      const pressedAt = performance.now();
      const showTimerId = setTimeout((): void => {
        if (!railHandlePending) {
          return;
        }
        showHoldRing(railHandlePending.curX, railHandlePending.curY, pressedAt);
      }, RAIL_HOLD_RING_DELAY_MS);
      railHandlePending = {
        obj,
        pointIndex: handle.index,
        startX: clientX,
        startY: clientY,
        curX: clientX,
        curY: clientY,
        timerId,
        showTimerId,
      };
      return;
    }
    const obj = field.selectedObject();
    if (!obj) {
      return;
    }
    const handleWorld = gizmo.scaleHandleWorld(handle.index);
    if (!handleWorld) {
      return;
    }
    rig.activeCamera.getWorldDirection(camDir);
    dragPlane.setFromNormalAndCoplanarPoint(camDir, handleWorld);
    if (obj.type === "box") {
      boxResizeDrag = new BoxCornerResizeDrag(obj, handle.index, !gizmoIs2D());
    } else if (obj.type === "array" || obj.type === "curve" || obj.type === "envelope" || obj.type === "pitchQuantize" || obj.type === "display") {
      seqResizeDrag = new SeqCornerResizeDrag(obj, handle.index);
    }
    scaling = true;
    scaleObj = obj;
    scaleHandleIndex = handle.index;
  }

  function startDrag(position: THREE.Vector3): void {
    const dragId = field.selectedObject()?.id ?? emitterField.selectedEmitter()?.id ?? null;
    if (dragId !== null && isClaimLocked(dragId)) {
      return;
    }
    setupDragPlane(position);
    const grab = dragPoint();
    dragOffset.set(0, 0, 0);
    if (grab) {
      dragOffset.copy(position).sub(grab);
    }
    dragging = true;
    dragMoved = false;
  }

  const DELETE_EDGE_PX = 48;
  type DeleteEdge = "top" | "right" | "bottom" | "left";
  const DELETE_EDGES: readonly DeleteEdge[] = ["top", "right", "bottom", "left"];
  const deleteEdgeEls = new Map<DeleteEdge, HTMLDivElement>();
  for (const edge of DELETE_EDGES) {
    const el = document.createElement("div");
    el.className = `simple-delete-edge ${edge}`;
    document.body.appendChild(el);
    deleteEdgeEls.set(edge, el);
  }

  /** The screen-edge delete band the pointer is in, or null. */
  function deleteEdgeAt(x: number, y: number): DeleteEdge | null {
    if (y <= DELETE_EDGE_PX) {
      return "top";
    }
    if (x >= window.innerWidth - DELETE_EDGE_PX) {
      return "right";
    }
    if (y >= window.innerHeight - DELETE_EDGE_PX) {
      return "bottom";
    }
    if (x <= DELETE_EDGE_PX) {
      return "left";
    }
    return null;
  }

  function updateDeleteEdgeHighlight(x: number, y: number): void {
    const active = deleteEdgeAt(x, y);
    for (const [edge, el] of deleteEdgeEls) {
      el.classList.toggle("visible", edge === active);
    }
  }

  function hideDeleteEdges(): void {
    for (const el of deleteEdgeEls.values()) {
      el.classList.remove("visible");
    }
  }

  const RAIL_MERGE_DIST = 0.24; // rail-local units, matches the gizmo handle box size

  function railOverlapIndex(points: readonly THREE.Vector2[], target: THREE.Vector2, skipIndex: number | null): number | null {
    for (let i = 0; i < points.length; i++) {
      if (i !== skipIndex && points[i].distanceTo(target) <= RAIL_MERGE_DIST) {
        return i;
      }
    }
    return null;
  }

  /**
   * Re-points the selection frame at the rail's freshly rebuilt mesh. Every
   * updateRailPoints call replaces the mesh, so without this the frame stays
   * parented to the disposed one and vanishes for the whole drag.
   */
  function refreshRailHalo(): void {
    const mesh = field.selectedMesh();
    selectionFrames.apply(mesh ? [{ mesh, color: SELECTION_FRAME_COLOR }] : []);
  }

  const RAIL_DUP_HOLD_MS = 1000;
  const RAIL_HOLD_RING_DELAY_MS = 200;
  const holdRingEl = document.createElement("span");
  holdRingEl.className = "fill-ring hold-ring-float";
  document.body.appendChild(holdRingEl);
  let holdRingRaf: number | null = null;
  let holdRingStart = 0;

  function tickHoldRing(): void {
    const t = Math.min((performance.now() - holdRingStart) / RAIL_DUP_HOLD_MS, 1);
    holdRingEl.style.background = `conic-gradient(${FILL_RING_COLOR} ${t * 360}deg, transparent 0deg)`;
    holdRingRaf = t < 1 ? requestAnimationFrame(tickHoldRing) : null;
  }

  function showHoldRing(x: number, y: number, startedAt: number): void {
    holdRingEl.style.left = `${x}px`;
    holdRingEl.style.top = `${y}px`;
    holdRingEl.classList.add("visible");
    holdRingStart = startedAt;
    if (holdRingRaf !== null) {
      cancelAnimationFrame(holdRingRaf);
    }
    holdRingRaf = requestAnimationFrame(tickHoldRing);
  }

  function hideHoldRing(): void {
    holdRingEl.classList.remove("visible");
    if (holdRingRaf !== null) {
      cancelAnimationFrame(holdRingRaf);
      holdRingRaf = null;
    }
  }

  const RAIL_TRASH_ICON_PX = 40;
  const railTrashEl = document.createElement("div");
  railTrashEl.className = "rail-trash";
  railTrashEl.innerHTML =
    `<svg viewBox="0 0 24 24" width="${RAIL_TRASH_ICON_PX}" height="${RAIL_TRASH_ICON_PX}"><path fill="currentColor" fill-rule="evenodd" ` +
    `d="M9 1.5h6V3h5v3H4V3h5V1.5zM5 7.5h14V21a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7.5z` +
    `m3.2 2.5h1.6v10.5H8.2V10zm3 0h1.6v10.5h-1.6V10zm3 0h1.6v10.5h-1.6V10z"/></svg>`;
  document.body.appendChild(railTrashEl);

  function showRailTrash(x: number, y: number): void {
    railTrashEl.style.left = `${x}px`;
    railTrashEl.style.top = `${y - RAIL_TRASH_ICON_PX * 1.5}px`;
    railTrashEl.classList.add("visible");
  }

  function hideRailTrash(): void {
    railTrashEl.classList.remove("visible");
  }

  /**
   * Screen position of one rail-local control point, for anchoring the trash
   * badge above the dragged point while a point drag is live.
   */
  function railPointScreen(obj: PlacedObject, p: THREE.Vector2): { x: number; y: number } {
    const world = new THREE.Vector3(p.x, p.y, 0).applyQuaternion(obj.rotation).add(obj.position);
    const ndc = world.project(rig.activeCamera);
    return { x: ((ndc.x + 1) / 2) * window.innerWidth, y: ((1 - ndc.y) / 2) * window.innerHeight };
  }

  const marqueeEl = document.createElement("div");
  marqueeEl.className = "marquee";
  marqueeEl.style.display = "none";
  const marqueeArrow = document.createElement("div");
  marqueeArrow.className = "marquee-arrow";
  marqueeArrow.innerHTML = `<div class="marquee-arrow-inner"><svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true"><path d="M4 12 H18 M12 6 L18 12 L12 18" fill="none" stroke="#cfeeff" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg></div>`;
  marqueeArrow.style.display = "none";
  marqueeEl.appendChild(marqueeArrow);
  document.body.appendChild(marqueeEl);
  let marqueeActive = false;
  let marqueePending = false;
  let marqueeDragging = false;
  const marqueeStart = { x: 0, y: 0 };
  const projVec = new THREE.Vector3();
  const marqueeBox = new THREE.Box3();

  function updateMarquee(x: number, y: number): void {
    marqueeEl.classList.remove("hint");
    marqueeArrow.style.display = "none";
    marqueeEl.style.left = `${Math.min(marqueeStart.x, x)}px`;
    marqueeEl.style.top = `${Math.min(marqueeStart.y, y)}px`;
    marqueeEl.style.width = `${Math.abs(x - marqueeStart.x)}px`;
    marqueeEl.style.height = `${Math.abs(y - marqueeStart.y)}px`;
  }

  /**
   * Draws a fixed-size square hinging on the anchored start point while a marquee
   * is pending. Touch has no hover, so a zero-size rect at the drop point would
   * be invisible; this hint marks the anchor and prompts the endpoint drag. The
   * box hangs off whichever start corner matches the start's screen quadrant and
   * extends toward screen centre; an arrow points along that diagonal so the eye
   * is led toward the drag direction.
   */
  function showMarqueeHint(): void {
    const sz = MARQUEE_HINT_PX;
    const left = marqueeStart.x < window.innerWidth / 2;
    const top = marqueeStart.y < window.innerHeight / 2;
    marqueeEl.classList.add("hint");
    marqueeEl.style.left = `${left ? marqueeStart.x : marqueeStart.x - sz}px`;
    marqueeEl.style.top = `${top ? marqueeStart.y : marqueeStart.y - sz}px`;
    marqueeEl.style.width = `${sz}px`;
    marqueeEl.style.height = `${sz}px`;
    const deg = top ? (left ? 45 : 135) : (left ? -45 : -135);
    const rad = (deg * Math.PI) / 180;
    const outX = Math.cos(rad) * MARQUEE_ARROW_OUTSET_PX;
    const outY = Math.sin(rad) * MARQUEE_ARROW_OUTSET_PX;
    marqueeArrow.style.left = deg === 45 || deg === -45 ? "100%" : "0%";
    marqueeArrow.style.top = deg === 45 || deg === 135 ? "100%" : "0%";
    marqueeArrow.style.transform =
      `translate(calc(-50% + ${outX}px), calc(-50% + ${outY}px)) rotate(${deg}deg)`;
    marqueeArrow.style.display = "";
    marqueeEl.style.display = "";
  }

  /**
   * Whether an entity can be wired as a transform child. Every placed object type and
   * every emitter exposes a transform-in pin, so any known entity qualifies; the marquee
   * additionally requires that pin to be free.
   */
  function entityAcceptsTransformIn(id: string): boolean {
    return objectById(id) !== null || emitterById(id) !== null;
  }

  function projectToScreen(pos: THREE.Vector3): { x: number; y: number; visible: boolean } {
    projVec.copy(pos).project(rig.activeCamera);
    return {
      x: (projVec.x * 0.5 + 0.5) * window.innerWidth,
      y: (-projVec.y * 0.5 + 0.5) * window.innerHeight,
      visible: projVec.z >= -1 && projVec.z <= 1,
    };
  }

  let lastCanvasCursor = "";

  /** Writes the canvas cursor only when it changes, so a high-Hz pointermove never re-touches the style (no forced style recalc per move). */
  function setCanvasCursor(cursor: string): void {
    if (cursor === lastCanvasCursor) {
      return;
    }
    lastCanvasCursor = cursor;
    canvas.style.cursor = cursor;
  }

  /**
   * Diagonal resize cursor for a scale handle, chosen from the handle's screen
   * offset from the object center: same-sign x/y (top-left / bottom-right) reads
   * as nwse, opposite signs (top-right / bottom-left) as nesw. Uses the already
   * projected handle/center world points — no layout read.
   */
  function resizeCursorForHandle(index: number): string {
    const handleWorld = gizmo.scaleHandleWorld(index);
    const sel = field.selectedObject();
    if (!handleWorld || !sel) {
      return "nwse-resize";
    }
    const hs = projectToScreen(handleWorld);
    const cs = projectToScreen(sel.position);
    const dx = hs.x - cs.x;
    const dy = hs.y - cs.y;
    return (dx >= 0) === (dy >= 0) ? "nwse-resize" : "nesw-resize";
  }

  /**
   * The canvas hover cursor for the current ray: a 3D widget control cap (knob /
   * button / array / curve / pitchQuantize) shows the finger, the selected body
   * (draggable) the move cross. Scale handles are DOM overlays that carry their
   * own resize cursor, so they are not considered here. Empty string = default.
   */
  function hoverCursor(): string {
    if (field.widgetControlHit(raycaster) !== null) {
      return "pointer";
    }
    const sel = gizmoSubject();
    if (sel !== null && mergedHits().some((h: MergedHit): boolean => h.item === sel)) {
      return "move";
    }
    return "";
  }

  const gizmoHandleLayer = document.createElement("div");
  gizmoHandleLayer.className = "gizmo-handle-layer";
  document.body.appendChild(gizmoHandleLayer);

  /**
   * A DOM overlay pool for one kind of gizmo grab handle (scale corner or rail
   * control point). `count`/`world` read the live handle set off the gizmo and
   * `cursor` supplies the per-handle hover cursor. A scale target is never also a
   * rail, so the two pools are never non-empty at once.
   */
  interface HandlePool {
    readonly kind: "scale" | "railPoint";
    readonly els: HTMLDivElement[];
    readonly cursors: string[];
    readonly shown: boolean[];
    count(): number;
    world(index: number): THREE.Vector3 | null;
    cursor(index: number): string;
  }

  /**
   * A DOM gizmo handle. Its pointerdown starts the same drag the mesh handle used
   * to, then captures the pointer on the canvas so the existing pointermove /
   * pointerup drag path (which reads the ray off the canvas) runs unchanged.
   * `index` is stable because a pool only grows/shrinks at its end.
   */
  function makeHandleEl(pool: HandlePool, index: number): HTMLDivElement {
    const el = document.createElement("div");
    el.className = "gizmo-handle";
    el.addEventListener("pointerdown", (ev: PointerEvent): void => {
      ev.preventDefault();
      ev.stopPropagation();
      if (!gizmo.hasTarget()) {
        return;
      }
      const subject = gizmoSubject();
      if (!subject || isClaimLocked(subject.id)) {
        return;
      }
      const handle: GizmoHandle = pool.kind === "scale" ? { kind: "scale", index } : { kind: "railPoint", index };
      startGizmoDrag(handle, ev.clientX, ev.clientY);
      canvas.setPointerCapture(ev.pointerId);
    });
    gizmoHandleLayer.appendChild(el);
    return el;
  }

  /**
   * Reconciles one DOM handle pool to the gizmo's live handle set. Grows/shrinks
   * the pool, then moves each live handle to its projected world point with
   * `transform` (never left/top, so no layout is forced) and a cursor set only on
   * change; handles projecting behind the camera hide.
   */
  function syncHandlePool(pool: HandlePool): void {
    const count = gizmo.hasTarget() ? pool.count() : 0;
    while (pool.els.length < count) {
      pool.els.push(makeHandleEl(pool, pool.els.length));
      pool.cursors.push("");
      pool.shown.push(true);
    }
    while (pool.els.length > count) {
      const el = pool.els.pop();
      pool.cursors.pop();
      pool.shown.pop();
      if (el) {
        el.remove();
      }
    }
    for (let i = 0; i < count; i++) {
      const el = pool.els[i];
      const world = pool.world(i);
      const screen = world ? projectToScreen(world) : null;
      if (!screen || !screen.visible) {
        if (pool.shown[i]) {
          el.style.display = "none";
          pool.shown[i] = false;
        }
        continue;
      }
      if (!pool.shown[i]) {
        el.style.display = "";
        pool.shown[i] = true;
      }
      el.style.transform = `translate(${screen.x}px, ${screen.y}px) translate(-50%, -50%)`;
      const cursor = pool.cursor(i);
      if (cursor !== pool.cursors[i]) {
        el.style.cursor = cursor;
        pool.cursors[i] = cursor;
      }
    }
  }

  const scalePool: HandlePool = {
    kind: "scale",
    els: [],
    cursors: [],
    shown: [],
    count: (): number => gizmo.scaleHandleCount(),
    world: (index: number): THREE.Vector3 | null => gizmo.scaleHandleWorld(index),
    cursor: (index: number): string => resizeCursorForHandle(index),
  };

  const railPool: HandlePool = {
    kind: "railPoint",
    els: [],
    cursors: [],
    shown: [],
    count: (): number => gizmo.railPointCount(),
    world: (index: number): THREE.Vector3 | null => gizmo.railPointWorld(index),
    cursor: (): string => "pointer",
  };

  /** Positions every DOM gizmo handle (scale + rail-point) for the frame. */
  function syncGizmoHandles(): void {
    syncHandlePool(scalePool);
    syncHandlePool(railPool);
  }

  /**
   * Entities whose projected screen-center lies inside the marquee rect and that can
   * still accept a transform-in (every placed object plus emitters) with a
   * free transform-in pin, so the marquee never steals an entity already parented to an
   * orbit / group.
   */
  function marqueeCapture(x: number, y: number): string[] {
    const minX = Math.min(marqueeStart.x, x);
    const maxX = Math.max(marqueeStart.x, x);
    const minY = Math.min(marqueeStart.y, y);
    const maxY = Math.max(marqueeStart.y, y);
    const ids: string[] = [];
    const consider = (id: string, pos: THREE.Vector3 | null): void => {
      if (!pos || !entityAcceptsTransformIn(id) || graph.edgesTo(id, "transformIn").length > 0) {
        return;
      }
      const sp = projectToScreen(pos);
      if (sp.visible && sp.x >= minX && sp.x <= maxX && sp.y >= minY && sp.y <= maxY) {
        ids.push(id);
      }
    };
    for (const o of field.list()) {
      consider(o.id, field.liveOrBasePosition(o.id));
    }
    for (const e of emitterField.list()) {
      consider(e.id, emitterField.liveOrBasePosition(e.id));
    }
    return ids;
  }

  function finalizeMarquee(x: number, y: number): void {
    const captured = marqueeCapture(x, y);
    if (captured.length === 0) {
      return;
    }
    marqueeBox.makeEmpty();
    for (const id of captured) {
      const p = field.liveOrBasePosition(id) ?? emitterField.liveOrBasePosition(id);
      if (p) {
        marqueeBox.expandByPoint(p);
      }
    }
    const center = new THREE.Vector3();
    marqueeBox.getCenter(center);
    pushUndo();
    const group = makeGroup(center);
    field.add(group);
    for (const id of captured) {
      graph.connect({ from: group.id, fromPort: "transformOut", to: id, toPort: "transformIn" });
    }
    persistObjects();
    persistConnections();
    disarmTool();
    emitterField.deselect();
    field.select(group);
    bindPanel();
  }

  /**
   * Console diagnostic for the "widgets suddenly un-pressable" heisenbug (clears
   * on reload). Every pointerdown early-returns before the widget hit test if any
   * of these global gates is stuck; each is pointer-independent, so calling
   * `window.__pointerGate()` from the console while frozen names the culprit
   * without a prior ?debug flag or a tap. A field reading true / non-null is a
   * gate currently swallowing pointerdowns.
   */
  (window as unknown as { __pointerGate: () => unknown }).__pointerGate = (): unknown => ({
    steady: rig.isSteady(),
    uiView: uiView(),
    marqueePending,
    railDraw: railDraw !== null,
    pendingWire: ports.pendingId(),
    tool: getActiveTool(),
  });

  canvas.addEventListener("pointerdown", (ev: PointerEvent): void => {
    audio.resume();
    if (ev.button !== 0 || !rig.isSteady()) {
      return;
    }
    if (uiView()) return;
    setRay(ev);
    objClickPending = false;
    if (marqueePending) {
      marqueeDragging = true;
      updateMarquee(ev.clientX, ev.clientY);
      rig.suspendControls();
      canvas.setPointerCapture(ev.pointerId);
      return;
    }
    if (railDraw) {
      if (deleteEdgeAt(ev.clientX, ev.clientY) !== null) {
        cancelRailDraw();
        setActiveTool(null);
        return;
      }
      if (raycaster.ray.intersectPlane(groundPlane, hit)) {
        const last = railDraw.points[railDraw.points.length - 1];
        if (last && last.distanceTo(hit) < RAIL_CONFIRM_DIST) {
          commitRailDraw();
          return;
        }
        addRailDrawPoint(hit);
      }
      return;
    }
    if (settings.simpleMode) {
      if (simplePointerDown(ev)) {
        canvas.setPointerCapture(ev.pointerId);
      }
      return;
    }
    const pendingSource = ports.pendingId();
    if (pendingSource !== null) {
      const hits = mergedHits();
      const acceptable = hits.filter((h: MergedHit): boolean =>
        h.item.id !== pendingSource && ports.canTargetAcceptPending(h.item.id));
      if (!ports.settlePending(acceptable.map((h: MergedHit): string => h.item.id))) {
        const front = hits.find((h: MergedHit): boolean => h.item.id !== pendingSource);
        if (front && convertValueToCountForPulse(front)) {
          ports.focusWireTarget(front.item.id);
          return;
        }
        ports.cancelPending();
      }
      return;
    }
    const tool = getActiveTool();
    if (tool === "group") {
      marqueeActive = true;
      marqueeStart.x = ev.clientX;
      marqueeStart.y = ev.clientY;
      updateMarquee(ev.clientX, ev.clientY);
      marqueeEl.style.display = "";
      canvas.setPointerCapture(ev.pointerId);
      return;
    }
    if (touchPlayOn()) {
      const hits = rawHits();
      const widgetHit = field.widgetControlHit(raycaster);
      if (widgetHit && (hits.length === 0 || hits[0].item === widgetHit.obj || widgetHit.dist <= hits[0].dist)) {
        beginWidgetOp(widgetHit.obj, widgetHit.paramName, ev);
        canvas.setPointerCapture(ev.pointerId);
        return;
      }
      const state: TouchPlayPointer = { lastObjId: null, railId: null, arcT: 0, volScale: 1 };
      touchPlayPointers.set(ev.pointerId, state);
      processTouchPlayHit(state);
      canvas.setPointerCapture(ev.pointerId);
      return;
    }
    const sel = field.selectedObject();
    if (sel || emitterField.selectedEmitter()) {
      const handle = gizmo.pick(raycaster);
      if (handle) {
        if (tryRotateReset(handle)) {
          canvas.setPointerCapture(ev.pointerId);
          return;
        }
        startGizmoDrag(handle, ev.clientX, ev.clientY);
        canvas.setPointerCapture(ev.pointerId);
        return;
      }
    }
    const merged = mergedHits(rawHits(ev));
    const widgetHit = field.widgetControlHit(raycaster);
    if (widgetHit && (merged.length === 0 || merged[0].item === widgetHit.obj || widgetHit.dist <= merged[0].dist)) {
      beginWidgetOp(widgetHit.obj, widgetHit.paramName, ev);
      canvas.setPointerCapture(ev.pointerId);
      return;
    }
    const cur = sel ?? emitterField.selectedEmitter();
    const curHit = cur !== null ? merged.find((h: MergedHit): boolean => h.item === cur) : undefined;
    if (curHit) {
      dragKind = curHit.kind;
      startDrag(curHit.item.position);
      objClickPending = true;
      canvas.setPointerCapture(ev.pointerId);
      return;
    }
    if (merged.length > 0) {
      objClickPending = true;
      canvas.setPointerCapture(ev.pointerId);
      return;
    }
    const hadSelection = selId() !== null;
    if (hadSelection) {
      pushSelectionUndo();
    }
    field.deselect();
    emitterField.deselect();
    bindPanel();
    if (!tool) {
      if (!hadSelection && isRadialMode() && !isTitleActive()) {
        radialTapPending = { pointerId: ev.pointerId, startX: ev.clientX, startY: ev.clientY };
      }
      return;
    }
    if (tool === "rail") {
      startRailDraw();
      if (raycaster.ray.intersectPlane(groundPlane, hit)) {
        addRailDrawPoint(hit);
      }
      return;
    }
    setupPlacePlane();
    const point = placePoint();
    if (!point) {
      return;
    }
    placing = true;
    canvas.setPointerCapture(ev.pointerId);
    if (isEmitterTool(tool)) {
      placingKind = "emitter";
      emitterField.beginPreview(emitterSpawn, point);
    } else {
      placingKind = "object";
      field.beginPreview(spawn, point);
    }
  });

  canvas.addEventListener("pointermove", (ev: PointerEvent): void => {
    lastPointerEv = ev;
    const widgetOp = widgetOps.get(ev.pointerId);
    if (widgetOp) {
      if (
        widgetOp.kind === "button" &&
        !touchPlayOn() &&
        Math.hypot(ev.clientX - widgetOp.startX, ev.clientY - widgetOp.startY) >= SIMPLE_TAP_SLOP_PX
      ) {
        cancelButtonPress(widgetOp.obj);
        widgetOps.delete(ev.pointerId);
        simplePress = { kind: "object", pointerId: ev.pointerId, startX: widgetOp.startX, startY: widgetOp.startY, moved: false, drillable: false };
      } else {
        updateWidgetOp(widgetOp, ev);
        return;
      }
    }
    if (railDraw) {
      setRay(ev);
      updateRailDrawPreview(raycaster.ray.intersectPlane(groundPlane, hit) ? hit.clone() : null);
      return;
    }
    const wireSource = ports.pendingId();
    if (wireSource !== null) {
      setRay(ev);
      const acceptableIds = mergedHits()
        .filter((h: MergedHit): boolean => h.item.id !== wireSource && ports.canTargetAcceptPending(h.item.id))
        .map((h: MergedHit): string => h.item.id);
      ports.updateWireHover(acceptableIds);
      return;
    }
    if (marqueeActive || marqueeDragging) {
      updateMarquee(ev.clientX, ev.clientY);
      return;
    }
    const touchPlayState = touchPlayPointers.get(ev.pointerId);
    if (touchPlayState) {
      setRay(ev);
      processTouchPlayHit(touchPlayState);
      return;
    }
    if (rotateDrag) {
      setRay(ev);
      const q = rotateDrag.update(raycaster.ray);
      if (q) {
        if (!gizmoMoved) {
          pushUndo();
          gizmoMoved = true;
        }
        applyGizmoRotation(q);
        gizmo.sync();
        rotateGuide?.setActive(rotateDrag.activeSnapIndex());
      }
      return;
    }
    if (railHandlePending) {
      railHandlePending.curX = ev.clientX;
      railHandlePending.curY = ev.clientY;
      if (Math.hypot(ev.clientX - railHandlePending.startX, ev.clientY - railHandlePending.startY) > 10) {
        clearTimeout(railHandlePending.timerId);
        clearTimeout(railHandlePending.showTimerId);
        hideHoldRing();
        const pending = railHandlePending;
        railHandlePending = null;
        railPointDrag = { obj: pending.obj, pointIndex: pending.pointIndex };
      } else {
        return;
      }
    }
    if (railDuplicateDrag) {
      setRay(ev);
      const point = dragPoint();
      if (point) {
        const dup = railDuplicateDrag;
        const local = snapMove(point).clone().sub(dup.obj.position).applyQuaternion(dup.obj.rotation.clone().conjugate());
        const dragLocal = new THREE.Vector2(local.x, local.y);
        const insertIdx = insertRailPointIndex(dup.originalPoints, dup.sourceIndex, dragLocal);
        const newPoints = dup.originalPoints.map((p: THREE.Vector2): THREE.Vector2 => p.clone());
        newPoints.splice(insertIdx, 0, dragLocal);
        field.updateRailPoints(dup.obj.id, newPoints);
        refreshRailHalo();
        dup.newIndex = insertIdx;
        gizmo.sync();
        if (railOverlapIndex(dup.originalPoints, dragLocal, null) !== null) {
          const pos = railPointScreen(dup.obj, dragLocal);
          showRailTrash(pos.x, pos.y);
        } else {
          hideRailTrash();
        }
      }
      updateDeleteEdgeHighlight(ev.clientX, ev.clientY);
      return;
    }
    if (railPointDrag) {
      setRay(ev);
      const point = dragPoint();
      if (point) {
        if (!gizmoMoved) {
          pushUndo();
          gizmoMoved = true;
        }
        const local = snapMove(point).clone().sub(railPointDrag.obj.position).applyQuaternion(
          railPointDrag.obj.rotation.clone().conjugate(),
        );
        const newPoints = railPointDrag.obj.railPoints.map(
          (p: THREE.Vector2, i: number): THREE.Vector2 =>
            i === railPointDrag!.pointIndex ? new THREE.Vector2(local.x, local.y) : p,
        );
        field.updateRailPoints(railPointDrag.obj.id, newPoints);
        refreshRailHalo();
        gizmo.sync();
      }
      const dragPoints = railPointDrag.obj.railPoints;
      if (dragPoints.length > 2) {
        updateDeleteEdgeHighlight(ev.clientX, ev.clientY);
        const dragged = dragPoints[railPointDrag.pointIndex];
        if (railOverlapIndex(dragPoints, dragged, railPointDrag.pointIndex) !== null) {
          const pos = railPointScreen(railPointDrag.obj, dragged);
          showRailTrash(pos.x, pos.y);
        } else {
          hideRailTrash();
        }
      }
      return;
    }
    if (scaling && scaleObj) {
      setRay(ev);
      const point = dragPoint();
      if (point) {
        if (!gizmoMoved) {
          pushUndo();
          gizmoMoved = true;
        }
        const grid = snapGrid(settings.editSnap);
        if (boxResizeDrag) {
          const { params, position } = boxResizeDrag.update(point, grid);
          field.moveSelected(position);
          field.editSelected({ params });
        } else if (seqResizeDrag) {
          const { patch, position } = seqResizeDrag.update(point, grid);
          field.moveSelected(position);
          field.editSelected(patch);
        } else {
          const lp = point.clone().sub(scaleObj.position).applyQuaternion(scaleObj.rotation.clone().conjugate());
          field.editSelected({ params: scaleParamsFromLocal(scaleObj, lp, scaleHandleIndex, grid) });
        }
        gizmo.sync();
      }
      return;
    }
    if (simplePress) {
      setRay(ev);
      const subject = gizmoSubject();
      if (!subject) {
        simplePress = null;
        return;
      }
      if (isClaimLocked(subject.id)) {
        return;
      }
      if (!simplePress.moved) {
        if (Math.hypot(ev.clientX - simplePress.startX, ev.clientY - simplePress.startY) < SIMPLE_TAP_SLOP_PX) {
          return;
        }
        simplePress.moved = true;
        pushUndo();
        setupDragPlane(subject.position);
        const grab = dragPoint();
        dragOffset.set(0, 0, 0);
        if (grab) {
          dragOffset.copy(subject.position).sub(grab);
        }
        return;
      }
      const point = dragPoint();
      if (point) {
        const rawCenter = point.add(dragOffset);
        if (simplePress.kind === "emitter") {
          emitterField.moveSelected(snapMove(rawCenter));
          particles.setEmitters(emitterField.list());
        } else {
          const obj = field.selectedObject();
          const pos = obj ? snapObjectMove(obj, rawCenter) : snapMove(rawCenter);
          if (obj && isTransformParent(obj.id)) {
            coMoveDescendants(obj, pos, obj.rotation);
          }
          field.moveSelected(pos);
        }
        gizmo.sync();
      }
      updateDeleteEdgeHighlight(ev.clientX, ev.clientY);
      return;
    }
    if (dragging) {
      setRay(ev);
      const point = dragPoint();
      if (point) {
        if (dragKind === "emitter") {
          if (!dragMoved) {
            pushUndo();
            dragMoved = true;
          }
          emitterField.moveSelected(snapMove(point.add(dragOffset)));
          particles.setEmitters(emitterField.list());
        } else {
          const obj = field.selectedObject();
          if (!dragMoved) {
            pushUndo();
            dragMoved = true;
            if (obj && isTransformParent(obj.id)) {
              captureDragOrigin(obj);
            }
          }
          const rawCenter = point.add(dragOffset);
          const newPos = obj ? snapObjectMove(obj, rawCenter) : snapMove(rawCenter);
          if (obj && isTransformParent(obj.id)) {
            coMoveDescendants(obj, newPos, obj.rotation);
          }
          field.moveSelected(newPos);
        }
      }
      if (dragMoved) {
        updateDeleteEdgeHighlight(ev.clientX, ev.clientY);
      }
      return;
    }
    if (!placing) {
      if (gizmo.hasTarget()) {
        setRay(ev);
        const handle = gizmo.pick(raycaster);
        gizmo.setHover(handle);
        setCanvasCursor(hoverCursor());
      } else if (getActiveTool()) {
        setRay(ev);
        setupPlacePlane();
        const point = placePoint();
        if (point) {
          if (placingKind === "emitter") {
            emitterField.updatePreview(snapMove(point));
          } else {
            field.updatePreview(snapMove(point));
          }
        }
        setCanvasCursor("");
      } else {
        setRay(ev);
        setCanvasCursor(hoverCursor());
      }
      return;
    }
    setRay(ev);
    const point = placePoint();
    if (point) {
      if (placingKind === "emitter") {
        emitterField.updatePreview(snapMove(point));
      } else {
        field.updatePreview(snapMove(point));
      }
    }
  });

  canvas.addEventListener("pointerup", (ev: PointerEvent): void => {
    if (radialTapPending !== null && radialTapPending.pointerId === ev.pointerId) {
      const pending = radialTapPending;
      radialTapPending = null;
      if (Math.hypot(ev.clientX - pending.startX, ev.clientY - pending.startY) < SIMPLE_TAP_SLOP_PX) {
        openRadial(pending.startX, pending.startY);
        return;
      }
    }
    const widgetOp = widgetOps.get(ev.pointerId);
    if (widgetOp) {
      widgetOps.delete(ev.pointerId);
      endWidgetOp(widgetOp, ev);
      return;
    }
    if (touchPlayPointers.delete(ev.pointerId)) {
      return;
    }
    if (marqueeDragging) {
      marqueeDragging = false;
      marqueePending = false;
      marqueeEl.style.display = "none";
      rig.resumeControls();
      finalizeMarquee(ev.clientX, ev.clientY);
      return;
    }
    if (marqueeActive) {
      marqueeActive = false;
      marqueeEl.style.display = "none";
      finalizeMarquee(ev.clientX, ev.clientY);
      return;
    }
    if (railHandlePending !== null) {
      clearTimeout(railHandlePending.timerId);
      clearTimeout(railHandlePending.showTimerId);
      railHandlePending = null;
      hideHoldRing();
      return;
    }
    if (railDuplicateDrag !== null) {
      const dup = railDuplicateDrag;
      railDuplicateDrag = null;
      hideDeleteEdges();
      hideRailTrash();
      const placed = dup.obj.railPoints[dup.newIndex];
      const cancelled = deleteEdgeAt(ev.clientX, ev.clientY) !== null ||
        railOverlapIndex(dup.originalPoints, placed, null) !== null;
      if (cancelled) {
        field.updateRailPoints(dup.obj.id, dup.originalPoints);
        gizmo.sync();
        bindPanel();
        persistObjects();
      } else {
        bindPanel();
        gizmoPersist();
      }
      gizmoMoved = false;
      return;
    }
    if (railPointDrag !== null) {
      const drag = railPointDrag;
      railPointDrag = null;
      hideDeleteEdges();
      hideRailTrash();
      const points = drag.obj.railPoints;
      const removable = gizmoMoved && points.length > 2 &&
        (deleteEdgeAt(ev.clientX, ev.clientY) !== null ||
          railOverlapIndex(points, points[drag.pointIndex], drag.pointIndex) !== null);
      if (removable) {
        field.updateRailPoints(
          drag.obj.id,
          points.filter((_: THREE.Vector2, i: number): boolean => i !== drag.pointIndex),
        );
        gizmo.sync();
      }
      if (gizmoMoved) {
        bindPanel();
        gizmoPersist();
      }
      return;
    }
    if (rotateDrag || scaling) {
      rotateDrag = null;
      clearRotateGuide();
      scaling = false;
      scaleObj = null;
      boxResizeDrag = null;
      seqResizeDrag = null;
      if (gizmoMoved) {
        bindPanel();
        gizmoPersist();
      }
      return;
    }
    if (simplePress) {
      const press = simplePress;
      simplePress = null;
      if (press.moved) {
        hideDeleteEdges();
        if (deleteEdgeAt(ev.clientX, ev.clientY) !== null) {
          const sel = field.selectedObject();
          if (sel && sel.type === "group") {
            resolveGroupEdgeDelete(sel, "all");
          } else {
            deleteSelectedEntity();
          }
        } else if (press.kind === "emitter") {
          syncEmitters();
        } else {
          persistObjects();
          if (field.selectedObject()?.type === "group") {
            syncEmitters();
          }
        }
      } else if (press.drillable) {
        cycleSelect(ev);
      }
      return;
    }
    if (dragging) {
      dragging = false;
      if (dragMoved) {
        hideDeleteEdges();
        if (deleteEdgeAt(ev.clientX, ev.clientY) !== null) {
          const sel = field.selectedObject();
          if (sel && sel.type === "group") {
            showGroupDeleteModal((choice: GroupDeleteChoice): void => resolveGroupEdgeDelete(sel, choice));
          } else {
            deleteSelectedEntity();
          }
        } else if (dragKind === "emitter") {
          syncEmitters();
        } else {
          persistObjects();
          if (field.selectedObject()?.type === "group") {
            syncEmitters();
          }
        }
      } else if (objClickPending) {
        cycleSelect(ev);
      }
      objClickPending = false;
      return;
    }
    if (!placing) {
      if (objClickPending) {
        cycleSelect(ev);
        objClickPending = false;
      }
      return;
    }
    placing = false;
    setRay(ev);
    const point = placePoint();
    if (placingKind === "emitter") {
      if (point) {
        emitterField.updatePreview(snapMove(point));
      }
      pushUndo();
      const placed = emitterField.commitPreview();
      if (placed) {
        field.deselect();
        emitterField.deselect();
        bindPanel();
        syncEmitters();
      } else {
        discardLastUndo();
      }
      showArmedPreview();
      return;
    }
    if (point) {
      field.updatePreview(snapMove(point));
    }
    pushUndo();
    const placed = field.commitPreview();
    if (placed) {
      field.deselect();
      emitterField.deselect();
      bindPanel();
      advanceSpawnPitchCycle();
      persistObjects();
    } else {
      discardLastUndo();
    }
    if (singleShotArmed) {
      singleShotArmed = false;
      setActiveTool(null);
    } else {
      showArmedPreview();
    }
  });

  canvas.addEventListener("pointercancel", (ev: PointerEvent): void => {
    if (radialTapPending !== null && radialTapPending.pointerId === ev.pointerId) {
      radialTapPending = null;
    }
    const widgetOp = widgetOps.get(ev.pointerId);
    if (widgetOp) {
      widgetOps.delete(ev.pointerId);
      endWidgetOp(widgetOp, null);
      return;
    }
    if (touchPlayPointers.delete(ev.pointerId)) {
      return;
    }
    if (marqueeDragging) {
      marqueeDragging = false;
      marqueePending = false;
      marqueeEl.style.display = "none";
      rig.resumeControls();
      return;
    }
    if (marqueeActive) {
      marqueeActive = false;
      marqueeEl.style.display = "none";
      return;
    }
    if (railHandlePending !== null) {
      clearTimeout(railHandlePending.timerId);
      clearTimeout(railHandlePending.showTimerId);
      railHandlePending = null;
      hideHoldRing();
      return;
    }
    if (railDuplicateDrag !== null) {
      field.updateRailPoints(railDuplicateDrag.obj.id, railDuplicateDrag.originalPoints);
      refreshRailHalo();
      gizmo.sync();
      railDuplicateDrag = null;
      hideDeleteEdges();
      hideRailTrash();
      gizmoMoved = false;
      return;
    }
    if (rotateDrag || scaling || railPointDrag !== null) {
      rotateDrag = null;
      clearRotateGuide();
      scaling = false;
      scaleObj = null;
      boxResizeDrag = null;
      seqResizeDrag = null;
      railPointDrag = null;
      hideDeleteEdges();
      hideRailTrash();
      return;
    }
    if (simplePress) {
      simplePress = null;
      hideDeleteEdges();
      return;
    }
    if (dragging) {
      dragging = false;
      hideDeleteEdges();
      return;
    }
    if (!placing) {
      return;
    }
    placing = false;
    showArmedPreview();
  });

  /**
   * deleteEdgeArmed: the palette sits inside the left delete band, so the
   * edge-cancel gesture only arms after the pointer has left every band once;
   * until then no band highlights and an edge drop places normally.
   */
  interface PaletteDrag {
    pointerId: number;
    tool: Tool | null;
    kind: "object" | "emitter" | "group" | "paste" | "rail" | "nodemenu";
    began: boolean;
    deleteEdgeArmed: boolean;
  }
  let paletteDrag: PaletteDrag | null = null;
  let nodeMenuPreviewing = false;
  let nodeMenuWasOpen = false;
  let singleShotArmed = false;
  let pastePreview: THREE.Group | null = null;

  /**
   * Builds the paste-drag ghost: one semi-transparent mesh per clipboard entity,
   * positioned relative to the clipboard basePoint so the group's position can
   * track the pointer. The group's z stays at basePoint.z, matching the paste
   * itself (x/y offset only, per-entity z preserved).
   */
  function buildPastePreview(cb: Clipboard): THREE.Group {
    const group = new THREE.Group();
    for (const entry of cb.objects) {
      const mesh = objectGhostMesh(entry);
      if (mesh) {
        mesh.position.sub(cb.basePoint);
        group.add(mesh);
      }
    }
    for (const entry of cb.emitters) {
      const mesh = emitterGhostMesh(entry);
      if (mesh) {
        mesh.position.sub(cb.basePoint);
        group.add(mesh);
      }
    }
    return group;
  }

  function removePastePreview(): void {
    if (!pastePreview) {
      return;
    }
    scene.remove(pastePreview);
    pastePreview.traverse((node: THREE.Object3D): void => {
      if (node instanceof THREE.Mesh) {
        node.geometry.dispose();
        (node.material as THREE.Material).dispose();
      }
    });
    pastePreview = null;
  }

  /**
   * Starts a one-shot drag-to-place gesture from a simple-palette button. The
   * button holds the pointer capture, so the gesture is tracked by the window
   * listeners below instead of the canvas handlers. On a plain click (no move)
   * the button only shows the drag-to-place hint and places nothing — both
   * top-level and node-submenu tools place exclusively by dragging onto the
   * canvas. `tapArms` keeps the node submenu open during a submenu-child gesture
   * (top-level buttons close it). The group tool is two-step instead: the drop
   * anchors a pending marquee, then a canvas press-drag sizes it and pointerup
   * finalizes.
   */
  /**
   * Programmatically places one entity of `tool` at a screen point, reusing the
   * palette spawn template + preview/commit path (so it mirrors the last-touched
   * peer's params and the spawn-pitch cycle). It pushes no undo of its own — the
   * caller (port-connect chooser) brackets creation + wiring in a single undo —
   * and never changes the current selection, so the wire source the ports overlay
   * is anchored to stays selected. Returns the new entity id, or null when the
   * point misses the place plane or the field rejects the add.
   */
  function createEntityAt(tool: Tool, clientX: number, clientY: number): string | null {
    if (tool === "group" || tool === "rail") {
      return null;
    }
    setRayAt(clientX, clientY);
    setupPlacePlane();
    const point = placePoint();
    if (!point) {
      return null;
    }
    const position = snapMove(point);
    if (isEmitterTool(tool)) {
      resetEmitterSpawn(tool);
      emitterField.beginPreview(emitterSpawn, position);
      const placed = emitterField.commitPreview();
      if (!placed) {
        return null;
      }
      syncEmitters();
      return placed.id;
    }
    resetObjectSpawn(tool);
    field.beginPreview(spawn, position);
    const placed = field.commitPreview();
    if (!placed) {
      return null;
    }
    advanceSpawnPitchCycle();
    persistObjects();
    return placed.id;
  }

  /**
   * Places one entity of `tool` at a screen point as a single undoable step,
   * used by the radial menu's generate/node sectors (which drop at the tap
   * anchor instead of dragging). Group / rail are gestural and can't drop at a
   * point, so they return false for the caller to arm instead. The opened undo
   * recording is discarded when the field rejects the add so no empty step lingers.
   */
  function placeToolAt(tool: Tool, clientX: number, clientY: number): boolean {
    if (tool === "group" || tool === "rail") {
      return false;
    }
    pushUndo();
    const id = createEntityAt(tool, clientX, clientY);
    if (id === null) {
      discardLastUndo();
      return false;
    }
    return true;
  }

  /**
   * Pastes the clipboard at a screen point as a single undoable step, used by
   * the radial menu's edit sector. Mirrors the paste-drag commit minus the drag.
   */
  function pasteAt(clientX: number, clientY: number): boolean {
    const clipboard = getClipboard();
    if (!clipboard) {
      return false;
    }
    setRayAt(clientX, clientY);
    setupPlacePlane();
    const point = placePoint();
    if (!point) {
      return false;
    }
    pushUndo();
    pasteClipboard(clipboard, point, graph, field, emitterField, snapGrid(settings.editSnap), gizmoIs2D());
    persistObjects();
    persistConnections();
    syncEmitters();
    bindPanel();
    return true;
  }

  /**
   * Seeds a gestural tool (rail / group) so its first point lands at the radial's
   * open anchor, then hands off to the normal canvas flow for the rest: a rail
   * starts a draw with its first node at the anchor (subsequent taps extend it);
   * a group arms a pending marquee whose first corner is the anchor (the next
   * canvas press-drag sizes it). Returns false for non-gestural tools.
   */
  function seedToolAtAnchor(tool: Tool, clientX: number, clientY: number): boolean {
    field.deselect();
    emitterField.deselect();
    bindPanel();
    if (tool === "rail") {
      cancelRailDraw();
      setRayAt(clientX, clientY);
      if (!raycaster.ray.intersectPlane(groundPlane, hit)) {
        return false;
      }
      startRailDraw();
      addRailDrawPoint(hit.clone());
      return true;
    }
    if (tool === "group") {
      marqueePending = true;
      marqueeStart.x = clientX;
      marqueeStart.y = clientY;
      showMarqueeHint();
      return true;
    }
    return false;
  }

  function beginPaletteDrag(tool: Tool, ev: PointerEvent, tapArms: boolean = false): void {
    if (marqueePending) {
      marqueePending = false;
      marqueeEl.style.display = "none";
    }
    if (!tapArms) {
      nodeSubmenu.close();
    }
    field.deselect();
    emitterField.deselect();
    bindPanel();
    if (isEmitterTool(tool)) {
      resetEmitterSpawn(tool);
      paletteDrag = { pointerId: ev.pointerId, tool, kind: "emitter", began: false, deleteEdgeArmed: false };
      return;
    }
    if (tool === "group") {
      paletteDrag = { pointerId: ev.pointerId, tool, kind: "group", began: false, deleteEdgeArmed: false };
      return;
    }
    if (tool === "rail") {
      cancelRailDraw();
      paletteDrag = { pointerId: ev.pointerId, tool, kind: "rail", began: false, deleteEdgeArmed: false };
      return;
    }
    resetObjectSpawn(tool);
    paletteDrag = { pointerId: ev.pointerId, tool, kind: "object", began: false, deleteEdgeArmed: false };
  }

  /**
   * Starts the node-parent gesture: the submenu is already open, and this single
   * captured drag drives both interactions. On pointermove it tracks the hovered
   * submenu child (highlighting it and configuring its spawn); dragging off the
   * palette onto the canvas previews that child for a drag-through drop. A plain
   * tap leaves the submenu open for a follow-up child tap.
   */
  function beginNodeMenuDrag(ev: PointerEvent, wasOpen: boolean): void {
    if (marqueePending) {
      marqueePending = false;
      marqueeEl.style.display = "none";
    }
    field.deselect();
    emitterField.deselect();
    bindPanel();
    nodeMenuPreviewing = false;
    nodeMenuWasOpen = wasOpen;
    paletteDrag = { pointerId: ev.pointerId, tool: null, kind: "nodemenu", began: false, deleteEdgeArmed: false };
  }

  /**
   * Configures the spawn template for a node-submenu child as a drag-through
   * hovers it. Mirrors beginPaletteDrag's per-tool setup for the placeable node
   * objects; group has no single-drop preview and is resolved on release.
   */
  function configureNodeMenuSpawn(tool: Tool): void {
    if (tool === "group" || tool === "rail" || isEmitterTool(tool)) {
      return;
    }
    resetObjectSpawn(tool);
  }


  function beginPasteDrag(ev: PointerEvent): void {
    if (!getClipboard()) {
      return;
    }
    if (marqueePending) {
      marqueePending = false;
      marqueeEl.style.display = "none";
    }
    nodeSubmenu.close();
    field.deselect();
    emitterField.deselect();
    bindPanel();
    paletteDrag = { pointerId: ev.pointerId, tool: null, kind: "paste", began: false, deleteEdgeArmed: false };
  }

  window.addEventListener("pointermove", (ev: PointerEvent): void => {
    if (!paletteDrag || ev.pointerId !== paletteDrag.pointerId) {
      return;
    }
    if (paletteDrag.kind === "nodemenu") {
      const hovered = nodeSubmenu.toolAt(ev.clientX, ev.clientY);
      if (hovered !== null && hovered !== paletteDrag.tool) {
        paletteDrag.tool = hovered;
        nodeSubmenu.setHover(hovered);
        configureNodeMenuSpawn(hovered);
      }
      const overMenu = isOverPalette(ev.clientX, ev.clientY);
      if (!overMenu && paletteDrag.tool !== null && paletteDrag.tool !== "group") {
        paletteDrag.began = true;
        setRay(ev);
        setupPlacePlane();
        const point = placePoint();
        if (point) {
          if (!nodeMenuPreviewing) {
            field.beginPreview(spawn, point);
            nodeMenuPreviewing = true;
          } else {
            field.updatePreview(snapMove(point));
          }
        }
      } else if (nodeMenuPreviewing) {
        field.cancelPreview();
        nodeMenuPreviewing = false;
      }
      return;
    }
    if (!paletteDrag.deleteEdgeArmed && deleteEdgeAt(ev.clientX, ev.clientY) === null) {
      paletteDrag.deleteEdgeArmed = true;
    }
    if (paletteDrag.deleteEdgeArmed) {
      updateDeleteEdgeHighlight(ev.clientX, ev.clientY);
    }
    if (paletteDrag.kind === "group") {
      paletteDrag.began = true;
      return;
    }
    if (paletteDrag.kind === "rail") {
      paletteDrag.began = true;
      setRay(ev);
      if (raycaster.ray.intersectPlane(groundPlane, hit)) {
        showRailStartPreview(snapMove(hit.clone()));
      } else {
        hideRailStartPreview();
      }
      return;
    }
    setRay(ev);
    setupPlacePlane();
    const point = placePoint();
    if (!point) {
      return;
    }
    if (paletteDrag.kind === "paste") {
      paletteDrag.began = true;
      const clipboard = getClipboard();
      if (!clipboard) {
        return;
      }
      if (!pastePreview) {
        pastePreview = buildPastePreview(clipboard);
        scene.add(pastePreview);
      }
      const ghostOrigin = snapMove(point);
      pastePreview.position.set(ghostOrigin.x, ghostOrigin.y, clipboard.basePoint.z);
      return;
    }
    if (!paletteDrag.began) {
      paletteDrag.began = true;
      if (paletteDrag.kind === "emitter") {
        emitterField.beginPreview(emitterSpawn, point);
      } else {
        field.beginPreview(spawn, point);
      }
      return;
    }
    if (paletteDrag.kind === "emitter") {
      emitterField.updatePreview(snapMove(point));
    } else {
      field.updatePreview(snapMove(point));
    }
  });

  /**
   * Whether a viewport point sits on the tool palette. The palette button holds
   * the pointer capture during a drag-to-place gesture (so ev.target is always
   * the button), hence the hit test goes through elementFromPoint: releasing
   * over the palette reads as "changed my mind", not as a placement.
   */
  function isOverPalette(x: number, y: number): boolean {
    const el = document.elementFromPoint(x, y);
    return el !== null && el.closest(".simple-palette") !== null;
  }

  window.addEventListener("pointerup", (ev: PointerEvent): void => {
    if (!paletteDrag || ev.pointerId !== paletteDrag.pointerId) {
      return;
    }
    const drag = paletteDrag;
    paletteDrag = null;
    hideDeleteEdges();
    hideRailStartPreview();
    if (drag.kind === "nodemenu") {
      nodeSubmenu.setHover(null);
      const overMenu = isOverPalette(ev.clientX, ev.clientY);
      if (!overMenu && nodeMenuPreviewing && drag.tool !== null) {
        setRay(ev);
        const point = placePoint();
        if (point) {
          field.updatePreview(snapMove(point));
        }
        pushUndo();
        const placed = field.commitPreview();
        if (placed) {
          advanceSpawnPitchCycle();
          persistObjects();
        } else {
          discardLastUndo();
        }
        nodeMenuPreviewing = false;
        return;
      }
      if (nodeMenuPreviewing) {
        field.cancelPreview();
        nodeMenuPreviewing = false;
      }
      if (drag.tool !== null) {
        showPaletteHint(drag.tool);
      } else if (nodeMenuWasOpen) {
        nodeSubmenu.close();
      }
      return;
    }
    if (!drag.began) {
      if (drag.tool) {
        showPaletteHint(drag.tool);
      }
      return;
    }
    if (drag.deleteEdgeArmed && deleteEdgeAt(ev.clientX, ev.clientY) !== null) {
      removePastePreview();
      field.cancelPreview();
      emitterField.cancelPreview();
      return;
    }
    if (drag.kind === "paste") {
      removePastePreview();
      const clipboard = getClipboard();
      if (!clipboard) {
        return;
      }
      setRay(ev);
      setupPlacePlane();
      const pastePoint = placePoint();
      if (!pastePoint) {
        return;
      }
      pushUndo();
      pasteClipboard(clipboard, pastePoint, graph, field, emitterField, snapGrid(settings.editSnap), gizmoIs2D());
      persistObjects();
      persistConnections();
      syncEmitters();
      bindPanel();
      return;
    }
    if (isOverPalette(ev.clientX, ev.clientY)) {
      field.cancelPreview();
      emitterField.cancelPreview();
      if (drag.tool) {
        showPaletteHint(drag.tool);
      }
      return;
    }
    if (drag.kind === "group") {
      marqueePending = true;
      marqueeStart.x = ev.clientX;
      marqueeStart.y = ev.clientY;
      showMarqueeHint();
      return;
    }
    if (drag.kind === "rail") {
      startRailDraw();
      setRay(ev);
      if (raycaster.ray.intersectPlane(groundPlane, hit)) {
        addRailDrawPoint(hit);
      }
      return;
    }
    setRay(ev);
    const point = placePoint();
    if (point) {
      if (drag.kind === "emitter") {
        emitterField.updatePreview(snapMove(point));
      } else {
        field.updatePreview(snapMove(point));
      }
    }
    pushUndo();
    const placed = drag.kind === "emitter" ? emitterField.commitPreview() : field.commitPreview();
    if (!placed) {
      discardLastUndo();
      return;
    }
    if (drag.kind === "emitter") {
      syncEmitters();
    } else {
      advanceSpawnPitchCycle();
      persistObjects();
    }
  });

  window.addEventListener("pointercancel", (ev: PointerEvent): void => {
    if (!paletteDrag || ev.pointerId !== paletteDrag.pointerId) {
      return;
    }
    paletteDrag = null;
    nodeMenuPreviewing = false;
    nodeSubmenu.setHover(null);
    hideDeleteEdges();
    hideRailStartPreview();
    removePastePreview();
    field.cancelPreview();
    emitterField.cancelPreview();
  });
  window.addEventListener("keydown", (ev: KeyboardEvent): void => {
    audio.resume();
    if (ev.ctrlKey || ev.metaKey) {
      const key = ev.key.toLowerCase();
      if (key === "z" && !ev.shiftKey) {
        ev.preventDefault();
        undo();
      } else if ((key === "z" && ev.shiftKey) || key === "y") {
        ev.preventDefault();
        redo();
      } else if (key === "c") {
        const active = document.activeElement;
        if (active instanceof HTMLInputElement || active instanceof HTMLSelectElement || active instanceof HTMLTextAreaElement) {
          return;
        }
        ev.preventDefault();
        const sel = field.selectedObject() ?? emitterField.selectedEmitter();
        if (sel) {
          setClipboard(buildClipboard(sel.id, graph, field, emitterField));
          syncCopyPasteButtons();
        }
      } else if (key === "v") {
        const active = document.activeElement;
        if (active instanceof HTMLInputElement || active instanceof HTMLSelectElement || active instanceof HTMLTextAreaElement) {
          return;
        }
        ev.preventDefault();
        const clipboard = getClipboard();
        if (clipboard && lastPointerEv) {
          setRay(lastPointerEv);
          setupPlacePlane();
          const point = placePoint();
          if (point) {
            pushUndo();
            pasteClipboard(clipboard, point, graph, field, emitterField, snapGrid(settings.editSnap), gizmoIs2D());
            persistObjects();
            persistConnections();
            syncEmitters();
            bindPanel();
          }
        }
      }
      return;
    }
    const el = document.activeElement;
    if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
      return;
    }
    if (ev.key === "Enter" && railDraw) {
      commitRailDraw();
      return;
    }
    if (ev.key === "Escape") {
      nodeSubmenu.close();
      if (railDraw) {
        cancelRailDraw();
        setActiveTool(null);
        return;
      }
      if (marqueePending) {
        marqueePending = false;
        if (marqueeDragging) {
          marqueeDragging = false;
          rig.resumeControls();
        }
        marqueeEl.style.display = "none";
        return;
      }
      if (ports.isPending()) {
        ports.cancelPending();
        return;
      }
      setActiveTool(null);
      return;
    }
  });

  function hitTestIds(ev: PointerEvent): string[] {
    setRay(ev);
    return mergedHits().map((h: MergedHit): string => h.item.id);
  }

  return { hitTestIds, showArmedPreview, hideArmedPreview, beginPaletteDrag, createEntityAt, placeToolAt, pasteAt, seedToolAtAnchor, beginNodeMenuDrag, beginPasteDrag, consumeTouchPlayOneShots, getTouchPlayRailInputs, clearTouchPlayState, syncGizmoHandles };
}
